import React, { useState, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { setStatusColorMap } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Plus, 
  Settings,
  Edit,
  Edit2,
  Save,
  Trash2,
  Loader2,
  Wrench,
  Building2,
  FileText,
  Download,
  Database,
  RotateCcw,
  CheckCircle,
  AlertTriangle,
  Mail,
  Bell,
  BellOff,
  Smartphone,
  BarChart3,
  Users,
  Clock,
  MousePointer,
  TrendingUp,
  ChevronUp,
  ChevronDown,
  GripVertical,
  Palette,
  Truck,
  Calendar,
  Eye,
  ClipboardList
} from 'lucide-react';
import { toast } from 'sonner';
import FieldFormOptionsPanel from '../components/FieldFormOptionsPanel';
import EquipmentPhotoUploader from '../components/EquipmentPhotoUploader';
import ChecklistTemplateEditor from '../components/settings/ChecklistTemplateEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Services() {
  const { isAdmin, isSuperUser, hasPermission } = useAuth();
  // Allow other pages to deep-link directly to a specific tab via ?tab=<name>
  // (e.g. /services?tab=equipment opens the new equipment form).
  const _initialTab = (() => {
    if (typeof window === 'undefined') return 'services';
    const t = new URLSearchParams(window.location.search).get('tab');
    return t || 'services';
  })();
  const [activeTab, setActiveTab] = useState(_initialTab);
  const [exporting, setExporting] = useState(false);
  const [backupStatus, setBackupStatus] = useState(null);
  const [loadingBackupStatus, setLoadingBackupStatus] = useState(false);
  const [onedriveStatus, setOnedriveStatus] = useState(null);
  const [cloudinaryScan, setCloudinaryScan] = useState(null);
  const [scanningCloudinary, setScanningCloudinary] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [fixingNames, setFixingNames] = useState(false);
  // Weekly full-content OneDrive export (2026-09-07)
  const [backupDestStatus, setBackupDestStatus] = useState(null);
  const [weeklyExportStatus, setWeeklyExportStatus] = useState(null);
  const [loadingWeeklyExportStatus, setLoadingWeeklyExportStatus] = useState(false);
  const [triggeringWeeklyExport, setTriggeringWeeklyExport] = useState(false);
  
  // Restore state
  const [restoring, setRestoring] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [selectedBackupForRestore, setSelectedBackupForRestore] = useState(null);
  const [restoreResult, setRestoreResult] = useState(null);
  const [backupPreview, setBackupPreview] = useState(null);       // {collections: {name: {count, restorable}}}
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selectedCollections, setSelectedCollections] = useState(new Set());
  // Local Upload Restore (bypasses OneDrive URL expiry entirely).
  // When `localUploadFiles` is non-null, the restore confirm dialog
  // POSTs to /export/backup/restore/upload (multipart) instead of
  // /export/backup/restore (JSON with backup_filename).
  const [localUploadFiles, setLocalUploadFiles] = useState(null);
  // Reconciliation state (post-mortem 2026-08-13 — for the buggy
  // Local Upload Restore that ignored the picker selection).
  const [reconcileRunning, setReconcileRunning] = useState(false);
  const [reconcileReport, setReconcileReport] = useState(null);
  const [reconcileCutoff, setReconcileCutoff] = useState('');
  // Dedupe (post-mortem 2026-08-13 — collections without an id field
  // got duplicated by every restore. This UI lets the owner preview
  // + safely remove existing duplicates keyed by natural fields.)
  const [dedupeCollection, setDedupeCollection] = useState('');
  const [dedupeCollections, setDedupeCollections] = useState([]);
  const [dedupeReport, setDedupeReport] = useState(null);
  const [dedupeRunning, setDedupeRunning] = useState(false);
  
  // Email settings state
  const [emailSettings, setEmailSettings] = useState({
    team_invite_emails: true,
    job_assignment_emails: true,
    job_status_change_emails: false
  });
  const [loadingEmailSettings, setLoadingEmailSettings] = useState(false);
  const [savingEmailSettings, setSavingEmailSettings] = useState(false);
  
  // Push notification state
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [subscribingPush, setSubscribingPush] = useState(false);

  // Event Types (calendar event categories)
  const [eventTypes, setEventTypes] = useState([]);
  const [eventTypeForm, setEventTypeForm] = useState({ name: '', color: '#dbeafe', sort_order: 99 });
  const [editingEventType, setEditingEventType] = useState(null);
  const [showEventTypeDialog, setShowEventTypeDialog] = useState(false);
  
  // Services state
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [showServiceDialog, setShowServiceDialog] = useState(false);
  const [showServiceDeleteDialog, setShowServiceDeleteDialog] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [savingService, setSavingService] = useState(false);
  const [serviceForm, setServiceForm] = useState({ 
    name: '', 
    description: '', 
    day_price: '',
    hourly_price: '',
    fixed_price: '',
    per_hole_price: '',
    night_price: '',
    linear_footage_price: '',
    active: true 
  });

  // Industry Classifications state
  const [classifications, setClassifications] = useState([]);
  const [loadingClassifications, setLoadingClassifications] = useState(true);
  const [showClassificationDialog, setShowClassificationDialog] = useState(false);
  const [showClassificationDeleteDialog, setShowClassificationDeleteDialog] = useState(false);
  const [selectedClassification, setSelectedClassification] = useState(null);
  const [savingClassification, setSavingClassification] = useState(false);
  const [classificationForm, setClassificationForm] = useState({ name: '', description: '', active: true });




  // Proposal Types state
  const [proposalTypes, setProposalTypes] = useState([]);
  const [loadingProposalTypes, setLoadingProposalTypes] = useState(true);
  const [showProposalTypeDialog, setShowProposalTypeDialog] = useState(false);
  const [showProposalTypeDeleteDialog, setShowProposalTypeDeleteDialog] = useState(false);
  const [selectedProposalType, setSelectedProposalType] = useState(null);
  const [savingProposalType, setSavingProposalType] = useState(false);
  const [proposalTypeForm, setProposalTypeForm] = useState({ name: '', description: '', active: true });

  // Job Statuses state
  const [jobStatuses, setJobStatuses] = useState([]);
  const [loadingJobStatuses, setLoadingJobStatuses] = useState(true);
  const [showJobStatusDialog, setShowJobStatusDialog] = useState(false);
  const [showJobStatusDeleteDialog, setShowJobStatusDeleteDialog] = useState(false);
  const [selectedJobStatus, setSelectedJobStatus] = useState(null);
  const [savingJobStatus, setSavingJobStatus] = useState(false);
  const [jobStatusForm, setJobStatusForm] = useState({ name: '', description: '', active: true, color: '#FDE68A' });

  // Equipment Categories state
  const [equipmentCategories, setEquipmentCategories] = useState([]);
  const [loadingEquipmentCategories, setLoadingEquipmentCategories] = useState(true);
  const [showEquipmentCategoryDialog, setShowEquipmentCategoryDialog] = useState(false);
  const [showEquipmentCategoryDeleteDialog, setShowEquipmentCategoryDeleteDialog] = useState(false);
  const [selectedEquipmentCategory, setSelectedEquipmentCategory] = useState(null);
  const [savingEquipmentCategory, setSavingEquipmentCategory] = useState(false);
  const [equipmentCategoryForm, setEquipmentCategoryForm] = useState({ name: '', description: '', active: true, custom_fields: [] });
  // Checklist templates library (21 seeded forms) — powers the three
  // per-subcategory dropdowns below. Fetched once on mount from
  // /api/checklist-templates. Sorted alphabetically by the backend.
  const [checklistTemplates, setChecklistTemplates] = useState([]);

  // Equipment Subcategories state (Feb 2026 — new layer under
  // equipment_categories that carries the three checklist template
  // linkages the 21 real forms need).
  const [equipmentSubcategories, setEquipmentSubcategories] = useState([]); // flat list, all parents
  const [expandedCategoryIds, setExpandedCategoryIds] = useState(new Set()); // categories with subcat panel open
  const [showSubcategoryDialog, setShowSubcategoryDialog] = useState(false);
  const [showSubcategoryDeleteDialog, setShowSubcategoryDeleteDialog] = useState(false);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [selectedSubcategoryParent, setSelectedSubcategoryParent] = useState(null); // parent category obj
  const [savingSubcategory, setSavingSubcategory] = useState(false);
  // Round 16 (2026-09-04) — read-only "View live checklist" preview so
  // office staff can self-verify current form content/wording without
  // touching a real asset's Check-Out flow or taking the agent's word
  // for it. Renders straight from `checklistTemplates` (already loaded
  // in full, including `sections` + `updated_at`) — no extra network call.
  const [previewSubcategory, setPreviewSubcategory] = useState(null);
  const [subcategoryForm, setSubcategoryForm] = useState({
    name: '', description: '', active: true,
    checkout_template_id: null, checkin_template_id: null,
  });

  // Analytics state
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [analyticsPeriod, setAnalyticsPeriod] = useState(30);

  // Maintenance Settings state
  const [maintenanceSettings, setMaintenanceSettings] = useState({ managers: [], manager_ids: [] });
  const [loadingMaintenanceSettings, setLoadingMaintenanceSettings] = useState(false);
  const [savingMaintenanceSettings, setSavingMaintenanceSettings] = useState(false);
  const [fieldReportSettings, setFieldReportSettings] = useState({ managers: [], manager_ids: [] });
  const [savingFieldReportSettings, setSavingFieldReportSettings] = useState(false);
  const [archiveRecipientSettings, setArchiveRecipientSettings] = useState({ managers: [], manager_ids: [] });
  const [savingArchiveRecipientSettings, setSavingArchiveRecipientSettings] = useState(false);
  const [cseRestrictions, setCseRestrictions] = useState({ submitter_ids: [], approver_ids: [], submitters: [], approvers: [] });
  const [savingCseRestrictions, setSavingCseRestrictions] = useState(false);
  const [allUsers, setAllUsers] = useState([]);

  // Job Edit Layout (role-based module visibility) state
  const [jobLayoutSettings, setJobLayoutSettings] = useState({ modules: {}, module_meta: [] });
  const [loadingJobLayout, setLoadingJobLayout] = useState(false);
  const [savingJobLayout, setSavingJobLayout] = useState(false);

  // Team list (for custom-field assignee picker on equipment)
  const [teamMembers, setTeamMembers] = useState([]);
  React.useEffect(() => {
    let mounted = true;
    axios.get(`${API}/team`)
      .then(r => { if (mounted) setTeamMembers(r.data || []); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    fetchServices();
    fetchClassifications();
    fetchProposalTypes();
    fetchJobStatuses();
    fetchEquipmentCategories();
    fetchChecklistTemplates();
    fetchEquipmentSubcategories();
    fetchEventTypes();
    fetchOverdueSettings();
    seedDefaultServices();
    seedDefaultClassifications();
    seedDefaultProposalTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const seedDefaultServices = async () => {
    try {
      await axios.post(`${API}/seed`);
    } catch (error) {
      console.error('Failed to seed services:', error);
    }
  };

  const seedDefaultClassifications = async () => {
    try {
      const response = await axios.get(`${API}/industry-classifications`);
      if (response.data.length === 0) {
        const defaults = [
          'Construction', 'Utilities', 'Residential', 'Commercial', 
          'Government', 'Industrial', 'Healthcare', 'Education', 'Retail', 'Other'
        ];
        for (const name of defaults) {
          await axios.post(`${API}/industry-classifications`, { name, active: true });
        }
        fetchClassifications();
      }
    } catch (error) {
      console.error('Failed to seed classifications:', error);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    } finally {
      setLoadingServices(false);
    }
  };

  const fetchClassifications = async () => {
    try {
      const response = await axios.get(`${API}/industry-classifications`);
      setClassifications(response.data);
    } catch (error) {
      console.error('Failed to fetch classifications:', error);
    } finally {
      setLoadingClassifications(false);
    }
  };

  const fetchProposalTypes = async () => {
    try {
      const response = await axios.get(`${API}/proposal-types`);
      setProposalTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch proposal types:', error);
    } finally {
      setLoadingProposalTypes(false);
    }
  };

  const fetchJobStatuses = async () => {
    try {
      const response = await axios.get(`${API}/job-statuses`);
      setJobStatuses(response.data);
    } catch (error) {
      console.error('Failed to fetch job statuses:', error);
    } finally {
      setLoadingJobStatuses(false);
    }
  };

  const fetchEquipmentCategories = async () => {
    try {
      const response = await axios.get(`${API}/assets/equipment-categories`);
      setEquipmentCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch equipment categories:', error);
    } finally {
      setLoadingEquipmentCategories(false);
    }
  };

  // Overdue Checkout Alerts threshold (days). Drives both the daily
  // manager auto-alert and the "Send Reminder" button on the Equipment list.
  const [overdueThreshold, setOverdueThreshold] = useState(3);
  const [savingOverdueThreshold, setSavingOverdueThreshold] = useState(false);
  const fetchOverdueSettings = async () => {
    try {
      const response = await axios.get(`${API}/assets/overdue-settings`);
      setOverdueThreshold(response.data.days_threshold ?? 3);
    } catch (error) {
      console.error('Failed to fetch overdue checkout settings:', error);
    }
  };
  const handleSaveOverdueThreshold = async () => {
    const n = parseInt(overdueThreshold, 10);
    if (!n || n < 1) {
      toast.error('Days must be at least 1');
      return;
    }
    setSavingOverdueThreshold(true);
    try {
      await axios.put(`${API}/assets/overdue-settings`, { days_threshold: n });
      toast.success('Overdue checkout threshold updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save threshold');
    } finally {
      setSavingOverdueThreshold(false);
    }
  };

  // Fetch the seeded 21-template library once on mount. Read-only for
  // this phase — no create/edit UI yet. Silently tolerates a failure
  // (dropdowns just render empty).
  const fetchChecklistTemplates = async () => {
    try {
      const response = await axios.get(`${API}/checklist-templates`);
      setChecklistTemplates(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch checklist templates:', error);
    }
  };

  // Subcategories — fetched once and grouped client-side per parent.
  // Passes include_inactive=true so admins can see + reactivate.
  const fetchEquipmentSubcategories = async () => {
    try {
      const response = await axios.get(`${API}/equipment-subcategories?include_inactive=true`);
      setEquipmentSubcategories(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch equipment subcategories:', error);
    }
  };

  const toggleCategoryExpansion = (categoryId) => {
    setExpandedCategoryIds(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      return next;
    });
  };

  const subcategoriesFor = (categoryId) =>
    equipmentSubcategories.filter(s => s.parent_category_id === categoryId);

  const handleAddSubcategory = (parentCategory) => {
    setSelectedSubcategoryParent(parentCategory);
    setSelectedSubcategory(null);
    setSubcategoryForm({
      name: '', description: '', active: true,
      checkout_template_id: null, checkin_template_id: null,
    });
    setShowSubcategoryDialog(true);
  };

  const handleEditSubcategory = (sub, parentCategory) => {
    setSelectedSubcategoryParent(parentCategory);
    setSelectedSubcategory(sub);
    setSubcategoryForm({
      name: sub.name || '',
      description: sub.description || '',
      active: sub.active !== false,
      checkout_template_id: sub.checkout_template_id || null,
      checkin_template_id: sub.checkin_template_id || null,
    });
    setShowSubcategoryDialog(true);
  };

  const handleSaveSubcategory = async () => {
    if (!subcategoryForm.name.trim()) {
      toast.error('Subcategory name is required');
      return;
    }
    setSavingSubcategory(true);
    try {
      if (selectedSubcategory) {
        // Update
        await axios.put(`${API}/equipment-subcategories/${selectedSubcategory.id}`, subcategoryForm);
        toast.success('Subcategory updated');
      } else {
        // Create
        await axios.post(`${API}/equipment-subcategories`, {
          ...subcategoryForm,
          parent_category_id: selectedSubcategoryParent.id,
        });
        toast.success('Subcategory created');
      }
      setShowSubcategoryDialog(false);
      fetchEquipmentSubcategories();
    } catch (error) {
      // Backend can return either a plain string detail or (for the
      // structured 400 shape) an object — coerce so `toast.error`
      // never receives a raw object.
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (detail?.message || 'Failed to save subcategory');
      toast.error(msg);
    } finally {
      setSavingSubcategory(false);
    }
  };

  const handleConfirmDeleteSubcategory = async () => {
    if (!selectedSubcategory) return;
    try {
      await axios.delete(`${API}/equipment-subcategories/${selectedSubcategory.id}`);
      toast.success('Subcategory deleted');
      setShowSubcategoryDeleteDialog(false);
      fetchEquipmentSubcategories();
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Structured 400 from the backend when assets reference this
      // subcategory — surface the friendly "deactivate instead" hint.
      if (detail && typeof detail === 'object' && detail.code === 'SUBCATEGORY_IN_USE') {
        toast.error(detail.message);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to delete subcategory');
      }
      setShowSubcategoryDeleteDialog(false);
    }
  };

  const fetchEventTypes = async () => {
    try {
      const response = await axios.get(`${API}/events/types/admin`);
      setEventTypes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch event types:', error);
    }
  };

  const openNewEventType = () => {
    setEditingEventType(null);
    setEventTypeForm({ name: '', color: '#dbeafe', sort_order: 99 });
    setShowEventTypeDialog(true);
  };

  const openEditEventType = (t) => {
    setEditingEventType(t);
    setEventTypeForm({
      name: t.name || '',
      color: t.color || '#dbeafe',
      sort_order: t.sort_order ?? 99,
    });
    setShowEventTypeDialog(true);
  };

  const handleSaveEventType = async () => {
    if (!eventTypeForm.name.trim()) {
      toast.error('Name is required');
      return;
    }
    try {
      if (editingEventType) {
        await axios.put(`${API}/events/types/${editingEventType.id}`, eventTypeForm);
        toast.success('Event type updated');
      } else {
        await axios.post(`${API}/events/types`, eventTypeForm);
        toast.success('Event type created');
      }
      setShowEventTypeDialog(false);
      setEditingEventType(null);
      fetchEventTypes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save event type');
    }
  };

  const handleToggleEventTypeActive = async (t) => {
    try {
      await axios.put(`${API}/events/types/${t.id}`, { active: !t.active });
      toast.success(t.active ? 'Event type hidden' : 'Event type re-enabled');
      fetchEventTypes();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleDeleteEventType = async (t) => {
    const confirmText = t.is_default
      ? `Default types cannot be permanently deleted. Hide "${t.name}" instead?`
      : `Delete event type "${t.name}"? Existing events using this type will keep the label but will need to be re-categorized.`;
    if (!window.confirm(confirmText)) return;
    try {
      const res = await axios.delete(`${API}/events/types/${t.id}`);
      toast.success(res.data?.message || 'Done');
      fetchEventTypes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    }
  };

  const seedDefaultProposalTypes = async () => {
    try {
      const response = await axios.get(`${API}/proposal-types`);
      if (response.data.length === 0) {
        const defaults = [
          { name: 'Locate', description: 'Utility locate services' },
          { name: 'Soft Dig', description: 'Soft dig / vacuum excavation' },
          { name: 'CCTV/Flush', description: 'CCTV inspection and flushing' }
        ];
        for (const type of defaults) {
          await axios.post(`${API}/proposal-types`, { ...type, active: true });
        }
        fetchProposalTypes();
      }
    } catch (error) {
      console.error('Failed to seed proposal types:', error);
    }
  };

  // Export functions
  const handleExportAll = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/export/all?format=json`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `fieldready_export_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Data exported successfully');
    } catch (error) {
      toast.error('Failed to export data');
      console.error('Export error:', error);
    } finally {
      setExporting(false);
    }
  };

  // Download the FULL nightly backup — every part streamed directly
  // from OneDrive to the browser via file-proxy. Sidesteps the
  // proxy/app-server timeout that broke the legacy `/export/all`
  // (which tried to serialize the entire DB in one request). Post-
  // mortem 2026-08-13.
  const [fullBackupProgress, setFullBackupProgress] = useState(null); // {current, total, filename}
  // Belt-and-suspenders click-guard: React state can lag behind rapid
  // repeat clicks (setState is async — a second click can fire before
  // fullBackupProgress renders as non-null). A ref is synchronous —
  // any click while `downloadInFlight.current` is true is dropped.
  const downloadInFlight = React.useRef(false);
  const handleDownloadFullBackup = async () => {
    if (downloadInFlight.current) {
      toast.info('A download is already in progress. Please wait for it to finish.');
      return;
    }
    downloadInFlight.current = true;
    setFullBackupProgress({ current: 0, total: 0, filename: null });
    try {
      // 1. Fetch the manifest
      const manifestResp = await axios.get(`${API}/export/backup/parts-manifest`);
      const manifest = manifestResp.data;
      const parts = manifest.parts || [];
      if (parts.length === 0) {
        toast.error('No backup parts available. Trigger a nightly backup first.');
        setFullBackupProgress(null);
        return;
      }
      if (manifest.missing_parts && manifest.missing_parts.length > 0) {
        toast.warning(`⚠ ${manifest.missing_parts.length} part(s) missing from OneDrive: ${manifest.missing_parts.slice(0, 3).join(', ')}${manifest.missing_parts.length > 3 ? '...' : ''}`);
      }

      // 2. Download each part sequentially via file-proxy. Each hidden
      //    <a download> tag opens a browser download; we wait between
      //    clicks so the browser doesn't drop them.
      for (let i = 0; i < parts.length; i++) {
        const p = parts[i];
        setFullBackupProgress({ current: i + 1, total: parts.length, filename: p.part_filename });
        const link = document.createElement('a');
        link.href = `${API}/onedrive/file-proxy/${encodeURIComponent(p.item_id)}`;
        link.setAttribute('download', p.part_filename);
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        link.remove();
        // Wait between clicks — most browsers throttle rapid downloads.
        await new Promise(r => setTimeout(r, 800));
      }

      toast.success(`Downloaded ${parts.length} backup parts to your Downloads folder`);
    } catch (error) {
      toast.error('Full backup download failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setFullBackupProgress(null);
      downloadInFlight.current = false;
    }
  };

  const handleExportCollection = async (collectionName, format = 'json') => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/export/collection/${collectionName}?format=${format}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `fieldready_${collectionName}_${new Date().toISOString().split('T')[0]}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${collectionName} exported successfully`);
    } catch (error) {
      toast.error(`Failed to export ${collectionName}`);
      console.error('Export error:', error);
    } finally {
      setExporting(false);
    }
  };

  // Backup functions
  const fetchBackupStatus = async () => {
    setLoadingBackupStatus(true);
    try {
      const response = await axios.get(`${API}/export/backup/status`);
      setBackupStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch backup status:', error);
    } finally {
      setLoadingBackupStatus(false);
    }
  };

  const fetchOnedriveStatus = async () => {
    try {
      const response = await axios.get(`${API}/onedrive/status`);
      setOnedriveStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch OneDrive status:', error);
    }
  };

  const handleConnectOnedrive = async () => {
    try {
      const response = await axios.get(`${API}/onedrive/connect`);
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error('Failed to initiate OneDrive connection');
    }
  };

  const handleDisconnectOnedrive = async () => {
    try {
      await axios.post(`${API}/onedrive/disconnect`);
      setOnedriveStatus({ connected: false });
      toast.success('OneDrive disconnected');
    } catch (error) {
      toast.error('Failed to disconnect OneDrive');
    }
  };

  // Weekly full-content export — backup destination connection + status/trigger
  const fetchBackupDestStatus = async () => {
    try {
      const response = await axios.get(`${API}/onedrive/backup-destination/status`);
      setBackupDestStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch backup destination status:', error);
    }
  };

  const handleConnectBackupDest = async () => {
    try {
      const response = await axios.get(`${API}/onedrive/connect-backup-destination`);
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error('Failed to initiate backup destination connection');
    }
  };

  const handleDisconnectBackupDest = async () => {
    try {
      await axios.post(`${API}/onedrive/backup-destination/disconnect`);
      setBackupDestStatus({ connected: false });
      toast.success('Backup destination disconnected');
    } catch (error) {
      toast.error('Failed to disconnect backup destination');
    }
  };

  const fetchWeeklyExportStatus = async () => {
    setLoadingWeeklyExportStatus(true);
    try {
      const response = await axios.get(`${API}/export/weekly-full-export/status`);
      setWeeklyExportStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch weekly export status:', error);
    } finally {
      setLoadingWeeklyExportStatus(false);
    }
  };

  const handleTriggerWeeklyExport = async () => {
    setTriggeringWeeklyExport(true);
    try {
      await axios.post(`${API}/export/weekly-full-export/trigger`);
      toast.success('Weekly export started — this can take a while for large folders. Refresh status below to check progress.');
    } catch (error) {
      toast.error('Failed to start weekly export');
    } finally {
      setTriggeringWeeklyExport(false);
    }
  };

  const handleDownloadWeeklyExport = async (exportId) => {
    try {
      // window.open() won't carry the Authorization header (JWT lives in
      // axios defaults, not a cookie) — fetch as a blob via axios instead
      // so the admin-only endpoint actually authenticates.
      const response = await axios.get(`${API}/export/weekly-full-export/download/${exportId}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'FieldReady_Full_Export.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download export');
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let val = bytes;
    let i = 0;
    while (val >= 1024 && i < units.length - 1) {
      val /= 1024;
      i++;
    }
    return `${val.toFixed(1)} ${units[i]}`;
  };

  const handleScanCloudinary = async () => {
    setScanningCloudinary(true);
    try {
      const response = await axios.get(`${API}/onedrive/migration/scan`);
      setCloudinaryScan(response.data);
    } catch (error) {
      toast.error('Failed to scan for Cloudinary files');
    } finally {
      setScanningCloudinary(false);
    }
  };

  const handleMigrateToOnedrive = async () => {
    if (!onedriveStatus?.connected) {
      toast.error('Connect OneDrive first');
      return;
    }
    setMigrating(true);
    try {
      await axios.post(`${API}/onedrive/migration/run`);
      toast.info('Migration started — this may take a few minutes...');
      const poll = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/onedrive/migration/status`);
          const s = res.data;
          if (s.done) {
            clearInterval(poll);
            setMigrating(false);
            if (s.failed > 0) {
              toast.error(`Migrated ${s.migrated} files, ${s.failed} failed. See details below.`, { duration: 10000 });
            } else if (s.migrated > 0) {
              toast.success(`Migrated ${s.migrated} files to OneDrive!`);
            } else {
              toast.info('No files needed migration.');
            }
            await handleScanCloudinary();
            if (s.errors?.length > 0) {
              setCloudinaryScan(prev => ({ ...prev, migration_errors: s.errors }));
            }
          }
        } catch {
          clearInterval(poll);
          setMigrating(false);
        }
      }, 3000);
    } catch (error) {
      toast.error('Migration failed: ' + (error.response?.data?.detail || error.message));
      setMigrating(false);
    }
  };

  const handleFixExtensions = async () => {
    setFixingNames(true);
    try {
      const response = await axios.post(`${API}/onedrive/migration/fix-extensions`);
      const data = response.data;
      if (data.fixed > 0) {
        toast.success(`Fixed ${data.fixed} file names in OneDrive.`);
      } else {
        toast.info('No files needed renaming.');
      }
    } catch (error) {
      toast.error('Fix failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setFixingNames(false);
    }
  };

  const [fixingUrls, setFixingUrls] = useState(false);
  const handleFixUrls = async () => {
    setFixingUrls(true);
    try {
      const response = await axios.post(`${API}/onedrive/migration/fix-urls`);
      const data = response.data;
      if (data.fixed > 0) {
        toast.success(`Fixed ${data.fixed} file URLs. Files are now viewable in-browser.`);
      } else {
        toast.info('All file URLs are already correct.');
      }
    } catch (error) {
      toast.error('Fix failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setFixingUrls(false);
    }
  };

  const handleTriggerBackup = async () => {
    setExporting(true);
    try {
      await axios.post(`${API}/export/backup/trigger`);
      toast.success('Backup completed successfully');
      fetchBackupStatus();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Unknown error';
      toast.error(`Backup failed: ${detail}`);
      console.error('Backup error:', error);
    } finally {
      setExporting(false);
    }
  };

  // Restore functions
  const handleRestoreClick = async (backup) => {
    setSelectedBackupForRestore(backup);
    setRestoreResult(null);
    setBackupPreview(null);
    setSelectedCollections(new Set());
    setLocalUploadFiles(null);
    setShowRestoreConfirm(true);

    // Fetch the backup's manifest so we can render the picker
    setPreviewLoading(true);
    try {
      const resp = await axios.post(`${API}/export/backup/preview`, {
        backup_filename: backup.filename,
      });
      setBackupPreview(resp.data);
      // Pre-select nothing — force explicit user choice
    } catch (error) {
      toast.error('Failed to preview backup: ' + (error.response?.data?.detail || error.message));
      setBackupPreview({ error: true });
    } finally {
      setPreviewLoading(false);
    }
  };

  // Local Upload Restore — reads user-selected JSON parts client-side
  // to build the same picker payload the URL preview endpoint returns,
  // then hands off to `handleRestoreConfirm` which dispatches to the
  // multipart upload endpoint. Fixes the OneDrive `downloadUrl` 1-hour
  // expiry problem: any backup file (any age) can be restored from disk.
  const handleLocalUploadSelect = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = ''; // allow re-selecting the same files later
    if (files.length === 0) return;

    setSelectedBackupForRestore({
      filename: `Local upload (${files.length} file${files.length === 1 ? '' : 's'})`,
      total_records: null,
      created_at: null,
      isLocalUpload: true,
    });
    setLocalUploadFiles(files);
    setRestoreResult(null);
    setBackupPreview(null);
    setSelectedCollections(new Set());
    setShowRestoreConfirm(true);
    setPreviewLoading(true);

    try {
      // Fetch the restorable set once so we can flag excluded collections
      // in the picker with the same UX as the URL-mode preview.
      let restorableSet = new Set();
      try {
        const setResp = await axios.get(`${API}/export/backup/restorable-set`);
        restorableSet = new Set(setResp.data.restorable || []);
      } catch (e) {
        // Fall back to allowing everything; the server still enforces
        // the exclusion list on the actual restore call.
      }

      // Parse each uploaded file client-side to build the collections map
      const collections = {};
      let earliestExportDate = null;
      let parseErrors = 0;
      for (const f of files) {
        try {
          const text = await f.text();
          const parsed = JSON.parse(text);
          if (parsed.export_date && (!earliestExportDate || parsed.export_date < earliestExportDate)) {
            earliestExportDate = parsed.export_date;
          }
          const parts = parsed.collections || {};
          for (const [name, meta] of Object.entries(parts)) {
            const count = (meta && typeof meta === 'object' && 'count' in meta) ? meta.count : 0;
            collections[name] = {
              count,
              restorable: restorableSet.size === 0 ? true : restorableSet.has(name),
            };
          }
        } catch (e) {
          parseErrors += 1;
          console.error('Failed to parse', f.name, e);
        }
      }

      if (parseErrors > 0 && Object.keys(collections).length === 0) {
        toast.error(`Could not parse any of the ${files.length} file(s). Are they valid backup JSON?`);
        setBackupPreview({ error: true });
      } else {
        if (parseErrors > 0) {
          toast.warning(`Skipped ${parseErrors} file(s) that were not valid JSON`);
        }
        setBackupPreview({
          collections,
          restorable_count: Object.values(collections).filter(c => c.restorable).length,
          total_collections: Object.keys(collections).length,
          parts_fetched: files.length - parseErrors,
          parts_total: files.length,
          export_date: earliestExportDate,
          isLocalUpload: true,
        });
        // Refresh the display name with the export date if we found one
        if (earliestExportDate) {
          setSelectedBackupForRestore(prev => ({
            ...prev,
            created_at: earliestExportDate,
          }));
        }
      }
    } finally {
      setPreviewLoading(false);
    }
  };
  // Reconciliation handler (post-mortem 2026-08-13 for the buggy
  // Local Upload Restore that ignored the picker selection). Uploads
  // every backup part JSON to a read-only admin endpoint that merges
  // them (same semantics as the restore endpoint) and returns a
  // per-collection list of docs whose _id ObjectId was generated
  // AFTER the specified cutoff — i.e. inserted by the buggy restore.
  const handleReconcileRestore = async (event) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = '';
    if (selected.length === 0) return;
    if (!reconcileCutoff) {
      toast.error('Enter the approximate UTC time of the buggy restore first');
      return;
    }
    setReconcileRunning(true);
    setReconcileReport(null);
    try {
      const form = new FormData();
      selected.forEach(f => form.append('files', f, f.name));
      form.append('restore_cutoff_iso', reconcileCutoff);
      const resp = await axios.post(
        `${API}/admin/reconcile/restore-report`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setReconcileReport(resp.data);
      toast.success(`Report ready — ${resp.data.totals.suspect_docs_total} suspect doc(s) across ${resp.data.totals.collections_scanned} collection(s) from ${resp.data.files_parsed}/${resp.data.files_uploaded} file(s)`);
    } catch (error) {
      toast.error('Reconciliation failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setReconcileRunning(false);
    }
  };

  // Dedupe handlers (post-mortem 2026-08-13). Fetches the list of
  // natural-key collections from the backend, then previews or
  // applies dedupe by grouping by natural key + keeping oldest _id.
  useEffect(() => {
    if (!isSuperUser) return;
    axios.get(`${API}/admin/reconcile/natural-key-collections`)
      .then(r => setDedupeCollections(r.data.collections || []))
      .catch(() => setDedupeCollections([]));
  }, [isSuperUser]);

  const runDedupe = async (confirm) => {
    if (!dedupeCollection) {
      toast.error('Pick a collection first');
      return;
    }
    if (confirm && !window.confirm(
      `Delete ${dedupeReport?.duplicate_count ?? '?'} duplicate row(s) from ${dedupeCollection}? ` +
      `The oldest ObjectId per natural-key group is KEPT. This cannot be undone.`
    )) return;
    setDedupeRunning(true);
    try {
      const form = new FormData();
      form.append('collection_name', dedupeCollection);
      form.append('confirm', confirm ? 'true' : 'false');
      const resp = await axios.post(
        `${API}/admin/reconcile/dedupe-collection`, form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setDedupeReport(resp.data);
      if (confirm) {
        toast.success(`Deleted ${resp.data.deleted} duplicate row(s) from ${dedupeCollection}`);
      } else {
        toast.info(`Preview: ${resp.data.duplicate_count} duplicate(s) across ${resp.data.duplicate_group_count} natural-key group(s)`);
      }
    } catch (error) {
      toast.error('Dedupe failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDedupeRunning(false);
    }
  };



  const handleRestoreConfirm = async () => {
    const isLocalUpload = Boolean(localUploadFiles && localUploadFiles.length > 0);
    if (!isLocalUpload && !selectedBackupForRestore?.filename) {
      toast.error('No backup selected');
      return;
    }
    if (selectedCollections.size === 0) {
      toast.error('Select at least one collection to restore');
      return;
    }

    setRestoring(true);
    setShowRestoreConfirm(false);

    try {
      let response;
      if (isLocalUpload) {
        // Multipart form: files[] + collections_to_restore (comma-sep)
        const form = new FormData();
        localUploadFiles.forEach(f => form.append('files', f, f.name));
        form.append('collections_to_restore', Array.from(selectedCollections).join(','));
        form.append('clear_existing', 'false');
        response = await axios.post(
          `${API}/export/backup/restore/upload`,
          form,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      } else {
        // Multi-part-aware AND scoped to only the collections the user
        // explicitly ticked in the picker. Fixes both the multi-part
        // bug and the "restore everything or nothing" UX limitation.
        response = await axios.post(`${API}/export/backup/restore`, {
          backup_filename: selectedBackupForRestore.filename,
          collections_to_restore: Array.from(selectedCollections),
          clear_existing: false
        });
      }

      setRestoreResult(response.data);
      toast.success(`Restored ${selectedCollections.size} collection(s) successfully`);
      setLocalUploadFiles(null);

      // Refresh the page data
      fetchServices();
      fetchClassifications();
      fetchProposalTypes();

    } catch (error) {
      toast.error('Restore failed: ' + (error.response?.data?.detail || error.message));
      console.error('Restore error:', error);
    } finally {
      setRestoring(false);
    }
  };

  // Fetch backup status when export tab is selected
  React.useEffect(() => {
    if (activeTab === 'export') {
      fetchBackupStatus();
      fetchOnedriveStatus();
      fetchBackupDestStatus();
      fetchWeeklyExportStatus();
    }
    // Check for OneDrive callback params
    const urlParams = new URLSearchParams(window.location.search);
    const onedriveParam = urlParams.get('onedrive');
    if (onedriveParam === 'connected') {
      toast.success('OneDrive connected successfully!');
      setActiveTab('export');
      fetchOnedriveStatus();
      window.history.replaceState({}, '', window.location.pathname);
    } else if (onedriveParam === 'error') {
      toast.error('Failed to connect OneDrive: ' + (urlParams.get('message') || 'Unknown error'));
      window.history.replaceState({}, '', window.location.pathname);
    }
    const onedriveBackupParam = urlParams.get('onedrive_backup');
    if (onedriveBackupParam === 'connected') {
      toast.success('Backup destination connected successfully!');
      setActiveTab('export');
      fetchBackupDestStatus();
      window.history.replaceState({}, '', window.location.pathname);
    } else if (onedriveBackupParam === 'error') {
      toast.error('Failed to connect backup destination: ' + (urlParams.get('message') || 'Unknown error'));
      window.history.replaceState({}, '', window.location.pathname);
    }
    // Allow deep-linking to a specific tab via ?tab=equipment etc.
    const tabParam = urlParams.get('tab');
    if (tabParam) {
      setActiveTab(tabParam);
    }
  }, [activeTab]);

  // Email settings functions
  const fetchEmailSettings = async () => {
    setLoadingEmailSettings(true);
    try {
      const response = await axios.get(`${API}/settings/email`);
      setEmailSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch email settings:', error);
    } finally {
      setLoadingEmailSettings(false);
    }
  };

  const handleEmailSettingToggle = async (setting) => {
    const newSettings = {
      ...emailSettings,
      [setting]: !emailSettings[setting]
    };
    setEmailSettings(newSettings);
    
    setSavingEmailSettings(true);
    try {
      await axios.put(`${API}/settings/email`, newSettings);
      toast.success('Email settings updated');
    } catch (error) {
      // Revert on error
      setEmailSettings(emailSettings);
      toast.error('Failed to update email settings');
    } finally {
      setSavingEmailSettings(false);
    }
  };

  // Fetch email settings when email tab is selected
  React.useEffect(() => {
    if (activeTab === 'email') {
      fetchEmailSettings();
      checkPushSupport();
    }
  }, [activeTab]);

  // Fetch analytics when analytics tab is selected
  React.useEffect(() => {
    if (activeTab === 'analytics') {
      fetchAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, analyticsPeriod]);

  // Fetch maintenance settings when tab is selected
  React.useEffect(() => {
    if (activeTab === 'maintenance-settings') {
      fetchMaintenanceSettings();
      fetchAllUsers();
    }
  }, [activeTab]);

  // Fetch job layout settings when the Job Edit Layout tab is selected
  React.useEffect(() => {
    if (activeTab === 'job-edit-layout') {
      fetchJobLayoutSettings();
    }
  }, [activeTab]);

  const fetchJobLayoutSettings = async () => {
    setLoadingJobLayout(true);
    try {
      const r = await axios.get(`${API}/job-layout-settings`);
      setJobLayoutSettings(r.data || { modules: {}, module_meta: [] });
    } catch (error) {
      console.error('Failed to fetch job layout settings:', error);
    } finally {
      setLoadingJobLayout(false);
    }
  };

  const handleJobLayoutToggle = (moduleKey, column) => {
    setJobLayoutSettings(prev => {
      const modules = { ...(prev.modules || {}) };
      const current = modules[moduleKey] || { global: true, admin: true, manager: true, technician: true };
      modules[moduleKey] = { ...current, [column]: !current[column] };
      return { ...prev, modules };
    });
  };

  const handleSaveJobLayout = async () => {
    setSavingJobLayout(true);
    try {
      const r = await axios.put(`${API}/job-layout-settings`, { modules: jobLayoutSettings.modules });
      setJobLayoutSettings(r.data || jobLayoutSettings);
      toast.success('Job edit layout saved');
    } catch (error) {
      console.error('Failed to save job layout:', error);
      toast.error(error.response?.data?.detail || 'Failed to save job layout');
    } finally {
      setSavingJobLayout(false);
    }
  };

  const fetchMaintenanceSettings = async () => {
    setLoadingMaintenanceSettings(true);
    try {
      const [m, fr, ar, cse] = await Promise.all([
        axios.get(`${API}/maintenance-settings`),
        axios.get(`${API}/field-report-settings`),
        axios.get(`${API}/field-reports-archive-settings`),
        axios.get(`${API}/cse-restrictions`),
      ]);
      setMaintenanceSettings(m.data);
      setFieldReportSettings(fr.data);
      setArchiveRecipientSettings(ar.data);
      setCseRestrictions(cse.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoadingMaintenanceSettings(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(`${API}/team`);
      // Show every active user — approver assignment is independent of role,
      // so a technician (e.g. Corey Parks) can be flagged as a Field Report
      // or Maintenance approver here. Inactive users are still excluded.
      const eligibleUsers = response.data.filter(u =>
        u.is_active !== false && u.active !== false
      );
      setAllUsers(eligibleUsers);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleSaveMaintenanceManagers = async (managerIds) => {
    setSavingMaintenanceSettings(true);
    try {
      const response = await axios.put(`${API}/maintenance-settings`, { manager_ids: managerIds }, {
        headers: { 'Content-Type': 'application/json' }
      });
      setMaintenanceSettings(response.data);
      // The backend silently strips blocked approver ids (e.g. Corey
      // Parks) rather than erroring — surface that here so the admin
      // isn't told "updated" when the specific toggle they clicked
      // didn't actually stick.
      const wasStripped = managerIds.some(id => !(response.data.manager_ids || []).includes(id));
      if (wasStripped) {
        toast.warning('Saved, but one selected approver is not allowed and was not added.');
      } else {
        toast.success('Maintenance managers updated');
      }
    } catch (error) {
      console.error('Failed to save maintenance settings:', error);
      toast.error('Failed to save maintenance managers');
    } finally {
      setSavingMaintenanceSettings(false);
    }
  };

  const toggleMaintenanceManager = (userId) => {
    const currentIds = maintenanceSettings.manager_ids || [];
    const newIds = currentIds.includes(userId)
      ? currentIds.filter(id => id !== userId)
      : [...currentIds, userId];
    handleSaveMaintenanceManagers(newIds);
  };

  const handleSaveFieldReportManagers = async (managerIds) => {
    setSavingFieldReportSettings(true);
    try {
      const response = await axios.put(`${API}/field-report-settings`, { manager_ids: managerIds }, {
        headers: { 'Content-Type': 'application/json' }
      });
      setFieldReportSettings(response.data);
      toast.success('Field Report managers updated');
    } catch (error) {
      console.error('Failed to save field report settings:', error);
      toast.error('Failed to save Field Report managers');
    } finally {
      setSavingFieldReportSettings(false);
    }
  };

  const toggleFieldReportManager = (userId) => {
    const currentIds = fieldReportSettings.manager_ids || [];
    const newIds = currentIds.includes(userId)
      ? currentIds.filter(id => id !== userId)
      : [...currentIds, userId];
    handleSaveFieldReportManagers(newIds);
  };

  // Weekly Field Reports Archive email recipients — a DELIBERATELY
  // separate list from Field Report approvers above (see backend
  // routes/services.py's comment on why: approval permission and "gets
  // the Saturday zip email" are different audiences).
  const handleSaveArchiveRecipients = async (managerIds) => {
    setSavingArchiveRecipientSettings(true);
    try {
      const response = await axios.put(`${API}/field-reports-archive-settings`, { manager_ids: managerIds }, {
        headers: { 'Content-Type': 'application/json' }
      });
      setArchiveRecipientSettings(response.data);
      toast.success('Weekly Archive recipients updated');
    } catch (error) {
      console.error('Failed to save archive recipient settings:', error);
      toast.error('Failed to save Weekly Archive recipients');
    } finally {
      setSavingArchiveRecipientSettings(false);
    }
  };

  const toggleArchiveRecipient = (userId) => {
    const currentIds = archiveRecipientSettings.manager_ids || [];
    const newIds = currentIds.includes(userId)
      ? currentIds.filter(id => id !== userId)
      : [...currentIds, userId];
    handleSaveArchiveRecipients(newIds);
  };

  // Confined Space Evaluation submitter/approver lists — client-editable,
  // any number of people on either list (no hard 1-person cap). The
  // "can't approve your own submission" rule is enforced per-report by
  // the backend at submit/approve time, not here (see
  // maintenance_permissions.py::_assert_can_approve) — a person can be
  // on BOTH lists simultaneously, they just can't self-approve a given
  // report they personally submitted.
  const handleSaveCseRestrictions = async (submitterIds, approverIds) => {
    setSavingCseRestrictions(true);
    try {
      const response = await axios.put(`${API}/cse-restrictions`, {
        submitter_ids: submitterIds,
        approver_ids: approverIds,
      }, {
        headers: { 'Content-Type': 'application/json' }
      });
      setCseRestrictions(response.data);
      toast.success('Confined Space permissions updated');
    } catch (error) {
      console.error('Failed to save CSE restrictions:', error);
      toast.error('Failed to save Confined Space permissions');
    } finally {
      setSavingCseRestrictions(false);
    }
  };

  const toggleCseSubmitter = (userId) => {
    const current = cseRestrictions.submitter_ids || [];
    const next = current.includes(userId) ? current.filter(id => id !== userId) : [...current, userId];
    handleSaveCseRestrictions(next, cseRestrictions.approver_ids || []);
  };

  const toggleCseApprover = (userId) => {
    const current = cseRestrictions.approver_ids || [];
    const next = current.includes(userId) ? current.filter(id => id !== userId) : [...current, userId];
    handleSaveCseRestrictions(cseRestrictions.submitter_ids || [], next);
  };

  // Phase-1 of the approvers consolidation: surface the two "Receive …
  // notifications" flags (already stored on each user's permission map
  // and editable from the Team page) on this Approvers screen so admins
  // can manage notification routing in one bulk-edit table instead of
  // jumping between Service + Team. Uses the surgical
  // PATCH /team/{id}/notification-flags endpoint so toggling here can
  // never overwrite the rest of the user's permissions.
  const toggleNotificationFlag = async (userId, flagKey) => {
    const user = allUsers.find(u => u.id === userId);
    if (!user) return;
    const current = (user.permissions || {})[flagKey];
    // Default for can_receive_maintenance_notifications is True, so
    // treat null/undefined as ON for that flag; the other defaults to
    // False, so undefined === OFF there. Matches backend defaults.
    const effective = current === undefined
      ? (flagKey === 'can_receive_maintenance_notifications')
      : Boolean(current);
    const next = !effective;
    // Optimistic update — patch the local cache first so the toggle
    // flips instantly; revert if the request fails.
    setAllUsers(prev => prev.map(u => u.id === userId
      ? { ...u, permissions: { ...(u.permissions || {}), [flagKey]: next } }
      : u));
    try {
      await axios.patch(`${API}/team/${userId}/notification-flags`, { [flagKey]: next });
    } catch (error) {
      console.error('Failed to toggle notification flag:', error);
      toast.error('Failed to update notification flag');
      // Revert local state on failure.
      setAllUsers(prev => prev.map(u => u.id === userId
        ? { ...u, permissions: { ...(u.permissions || {}), [flagKey]: effective } }
        : u));
    }
  };

  const fetchAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const response = await axios.get(`${API}/analytics/summary?days=${analyticsPeriod}`);
      setAnalyticsData(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoadingAnalytics(false);
    }
  };




  // Push notification functions
  const checkPushSupport = async () => {
    // Check if push notifications are supported
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setPushSupported(supported);
    
    if (supported) {
      // Check current permission
      setPushPermission(Notification.permission);
      
      // Check if already subscribed
      try {
        const response = await axios.get(`${API}/push/status`);
        setPushSubscribed(response.data.enabled);
      } catch (error) {
        console.error('Failed to check push status:', error);
      }
    }
  };

  const subscribeToPush = async () => {
    setSubscribingPush(true);
    try {
      // Check if we're in a PWA context (standalone mode)
      const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                    window.navigator.standalone === true;
      
      if (!isPWA) {
        toast.error('Please open FieldReady from your Home Screen (not Safari) to enable notifications');
        setSubscribingPush(false);
        return;
      }
      
      // Check if Notification API exists
      if (!('Notification' in window)) {
        toast.error('Notifications not supported on this device');
        setSubscribingPush(false);
        return;
      }
      
      // Request notification permission
      console.log('Requesting notification permission...');
      const permission = await Notification.requestPermission();
      console.log('Permission result:', permission);
      setPushPermission(permission);
      
      if (permission !== 'granted') {
        if (permission === 'denied') {
          toast.error('Notifications blocked. Go to Settings → FieldReady → Notifications to enable.');
        } else {
          toast.error('Notification permission not granted');
        }
        setSubscribingPush(false);
        return;
      }
      
      // Get VAPID public key from server
      const keyResponse = await axios.get(`${API}/push/vapid-public-key`);
      const vapidPublicKey = keyResponse.data.publicKey;
      
      // Convert VAPID key to Uint8Array
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
      };
      
      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;
      
      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });
      
      // Send subscription to server
      await axios.post(`${API}/push/subscribe`, {
        subscription: subscription.toJSON()
      });
      
      setPushSubscribed(true);
      toast.success('Push notifications enabled! You will now receive alerts.');
      
    } catch (error) {
      console.error('Push subscription error:', error);
      toast.error('Failed to enable push notifications: ' + (error.message || 'Unknown error'));
    } finally {
      setSubscribingPush(false);
    }
  };

  const testPushNotification = async () => {
    try {
      await axios.post(`${API}/push/test`);
      toast.success('Test notification sent!');
    } catch (error) {
      toast.error('Failed to send test notification');
    }
  };

  const unsubscribeFromPush = async () => {
    setSubscribingPush(true);
    try {
      // Get current service worker registration
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        // Unsubscribe from browser
        await subscription.unsubscribe();
        
        // Remove from server
        await axios.delete(`${API}/push/unsubscribe`, {
          data: { subscription: subscription.toJSON() }
        });
      }
      
      setPushSubscribed(false);
      toast.success('Push notifications disabled');
    } catch (error) {
      console.error('Unsubscribe error:', error);
      toast.error('Failed to disable notifications');
    } finally {
      setSubscribingPush(false);
    }
  };

  // Redirect if no access (must be admin, super user, or have any one of these management perms)
  const _hasAnyAccess = isAdmin() || isSuperUser?.() ||
    hasPermission('manage_services') ||
    hasPermission('manage_equipment') ||
    hasPermission('manage_team');
  if (!_hasAnyAccess) {
    return <Navigate to="/" replace />;
  }

  // Service handlers
  const handleAddService = () => {
    setSelectedService(null);
    setServiceForm({ name: '', description: '', day_price: '', hourly_price: '', fixed_price: '', per_hole_price: '', night_price: '', linear_footage_price: '', active: true });
    setShowServiceDialog(true);
  };

  const handleEditService = (service) => {
    setSelectedService(service);
    setServiceForm({
      name: service.name,
      description: service.description || '',
      day_price: service.day_price || '',
      hourly_price: service.hourly_price || '',
      fixed_price: service.fixed_price || '',
      per_hole_price: service.per_hole_price || '',
      night_price: service.night_price || '',
      linear_footage_price: service.linear_footage_price || '',
      active: service.active
    });
    setShowServiceDialog(true);
  };

  const handleSaveService = async () => {
    if (!serviceForm.name) {
      toast.error('Service name is required');
      return;
    }
    
    // Convert price strings to numbers
    const payload = {
      ...serviceForm,
      day_price: serviceForm.day_price ? parseFloat(serviceForm.day_price) : null,
      hourly_price: serviceForm.hourly_price ? parseFloat(serviceForm.hourly_price) : null,
      fixed_price: serviceForm.fixed_price ? parseFloat(serviceForm.fixed_price) : null,
      per_hole_price: serviceForm.per_hole_price ? parseFloat(serviceForm.per_hole_price) : null,
      night_price: serviceForm.night_price ? parseFloat(serviceForm.night_price) : null,
      linear_footage_price: serviceForm.linear_footage_price ? parseFloat(serviceForm.linear_footage_price) : null
    };
    
    setSavingService(true);
    try {
      if (selectedService) {
        await axios.put(`${API}/services/${selectedService.id}`, payload);
        toast.success('Service updated');
      } else {
        await axios.post(`${API}/services`, payload);
        toast.success('Service created');
      }
      setShowServiceDialog(false);
      await fetchServices();
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Save failed: ${msg}. Please try again.`, { duration: 5000 });
    } finally {
      setSavingService(false);
    }
  };

  const handleDeleteService = async () => {
    setSavingService(true);
    try {
      await axios.delete(`${API}/services/${selectedService.id}`);
      toast.success('Service deleted');
      setShowServiceDeleteDialog(false);
      setSelectedService(null);
      fetchServices();
    } catch (error) {
      toast.error('Failed to delete service');
    } finally {
      setSavingService(false);
    }
  };

  // Classification handlers
  const handleAddClassification = () => {
    setSelectedClassification(null);
    setClassificationForm({ name: '', description: '', active: true });
    setShowClassificationDialog(true);
  };

  const handleEditClassification = (classification) => {
    setSelectedClassification(classification);
    setClassificationForm({
      name: classification.name,
      description: classification.description || '',
      active: classification.active
    });
    setShowClassificationDialog(true);
  };

  const handleSaveClassification = async () => {
    if (!classificationForm.name) {
      toast.error('Classification name is required');
      return;
    }
    setSavingClassification(true);
    try {
      if (selectedClassification) {
        await axios.put(`${API}/industry-classifications/${selectedClassification.id}`, classificationForm);
        toast.success('Classification updated');
      } else {
        await axios.post(`${API}/industry-classifications`, classificationForm);
        toast.success('Classification created');
      }
      setShowClassificationDialog(false);
      fetchClassifications();
    } catch (error) {
      toast.error('Failed to save classification');
    } finally {
      setSavingClassification(false);
    }
  };

  const handleDeleteClassification = async () => {
    setSavingClassification(true);
    try {
      await axios.delete(`${API}/industry-classifications/${selectedClassification.id}`);
      toast.success('Classification deleted');
      setShowClassificationDeleteDialog(false);
      setSelectedClassification(null);
      fetchClassifications();
    } catch (error) {
      toast.error('Failed to delete classification');
    } finally {
      setSavingClassification(false);
    }
  };

  // Proposal Type handlers
  const handleAddProposalType = () => {
    setSelectedProposalType(null);
    setProposalTypeForm({ name: '', description: '', active: true });
    setShowProposalTypeDialog(true);
  };

  const handleEditProposalType = (proposalType) => {
    setSelectedProposalType(proposalType);
    setProposalTypeForm({
      name: proposalType.name,
      description: proposalType.description || '',
      active: proposalType.active
    });
    setShowProposalTypeDialog(true);
  };

  const handleSaveProposalType = async () => {
    if (!proposalTypeForm.name) {
      toast.error('Proposal type name is required');
      return;
    }
    setSavingProposalType(true);
    try {
      if (selectedProposalType) {
        await axios.put(`${API}/proposal-types/${selectedProposalType.id}`, proposalTypeForm);
        toast.success('Proposal type updated');
      } else {
        await axios.post(`${API}/proposal-types`, proposalTypeForm);
        toast.success('Proposal type created');
      }
      setShowProposalTypeDialog(false);
      fetchProposalTypes();
    } catch (error) {
      toast.error('Failed to save proposal type');
    } finally {
      setSavingProposalType(false);
    }
  };

  const handleDeleteProposalType = async () => {
    setSavingProposalType(true);
    try {
      await axios.delete(`${API}/proposal-types/${selectedProposalType.id}`);
      toast.success('Proposal type deleted');
      setShowProposalTypeDeleteDialog(false);
      setSelectedProposalType(null);
      fetchProposalTypes();
    } catch (error) {
      toast.error('Failed to delete proposal type');
    } finally {
      setSavingProposalType(false);
    }
  };

  // Job Status handlers
  const handleAddJobStatus = () => {
    setSelectedJobStatus(null);
    setJobStatusForm({ name: '', description: '', active: true, color: '#FDE68A' });
    setShowJobStatusDialog(true);
  };

  const handleEditJobStatus = (jobStatus) => {
    setSelectedJobStatus(jobStatus);
    setJobStatusForm({
      name: jobStatus.name,
      description: jobStatus.description || '',
      active: jobStatus.active,
      color: jobStatus.color || '#FDE68A'
    });
    setShowJobStatusDialog(true);
  };

  const handleSaveJobStatus = async () => {
    if (!jobStatusForm.name) {
      toast.error('Job status name is required');
      return;
    }
    setSavingJobStatus(true);
    try {
      if (selectedJobStatus) {
        await axios.put(`${API}/job-statuses/${selectedJobStatus.id}`, jobStatusForm);
        toast.success('Job status updated');
      } else {
        await axios.post(`${API}/job-statuses`, jobStatusForm);
        toast.success('Job status created');
      }
      setShowJobStatusDialog(false);
      // Refresh local list AND the global color cache so every Badge updates immediately
      const refreshed = await axios.get(`${API}/job-statuses`);
      setJobStatuses(refreshed.data || []);
      setStatusColorMap(refreshed.data || []);
    } catch (error) {
      toast.error('Failed to save job status');
    } finally {
      setSavingJobStatus(false);
    }
  };

  const handleDeleteJobStatus = async () => {
    setSavingJobStatus(true);
    try {
      await axios.delete(`${API}/job-statuses/${selectedJobStatus.id}`);
      toast.success('Job status deleted');
      setShowJobStatusDeleteDialog(false);
      setSelectedJobStatus(null);
      fetchJobStatuses();
    } catch (error) {
      toast.error('Failed to delete job status');
    } finally {
      setSavingJobStatus(false);
    }
  };

  const handleReorderJobStatuses = async (dragIndex, dropIndex) => {
    const newStatuses = [...jobStatuses];
    const [removed] = newStatuses.splice(dragIndex, 1);
    newStatuses.splice(dropIndex, 0, removed);
    
    // Optimistic update
    setJobStatuses(newStatuses);
    
    // Send new order to backend
    try {
      await axios.put(`${API}/job-statuses/reorder`, {
        status_ids: newStatuses.map(s => s.id)
      });
      toast.success('Order saved');
    } catch (error) {
      toast.error('Failed to reorder');
      fetchJobStatuses(); // Revert on error
    }
  };

  const moveJobStatus = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= jobStatuses.length) return;
    handleReorderJobStatuses(index, newIndex);
  };

  // Equipment Category handlers
  const handleAddEquipmentCategory = () => {
    setSelectedEquipmentCategory(null);
    setEquipmentCategoryForm({ name: '', description: '', active: true, field_use: true, custom_fields: [] });
    setShowEquipmentCategoryDialog(true);
  };

  const handleEditEquipmentCategory = (category) => {
    setSelectedEquipmentCategory(category);
    setEquipmentCategoryForm({
      name: category.name,
      description: category.description || '',
      active: category.active,
      // Pre-Feb-2026 rows have no field_use — treat missing as true
      // so the toggle initialises "on" instead of "off".
      field_use: category.field_use !== false,
      custom_fields: Array.isArray(category.custom_fields) ? category.custom_fields : [],
    });
    setShowEquipmentCategoryDialog(true);
  };

  // Custom-field editors (used inside the category dialog)
  const addCustomField = () => {
    setEquipmentCategoryForm(prev => ({
      ...prev,
      custom_fields: [
        ...(prev.custom_fields || []),
        { id: `tmp-${Date.now()}`, label: '', field_type: 'text', required: false, options: [], reminder_days_before: null }
      ]
    }));
  };

  const updateCustomField = (index, patch) => {
    setEquipmentCategoryForm(prev => {
      const next = [...(prev.custom_fields || [])];
      next[index] = { ...next[index], ...patch };
      return { ...prev, custom_fields: next };
    });
  };

  const removeCustomField = (index) => {
    setEquipmentCategoryForm(prev => {
      const next = [...(prev.custom_fields || [])];
      next.splice(index, 1);
      return { ...prev, custom_fields: next };
    });
  };

  const handleSaveEquipmentCategory = async () => {
    if (!equipmentCategoryForm.name) {
      toast.error('Category name is required');
      return;
    }
    // Clean custom fields: drop empty labels, normalize options
    const cleanedFields = (equipmentCategoryForm.custom_fields || [])
      .filter(f => (f.label || '').trim().length > 0)
      .map(f => {
        const cleaned = {
          ...(f.id && !f.id.startsWith('tmp-') ? { id: f.id } : {}),
          label: f.label.trim(),
          field_type: f.field_type || 'text',
          required: !!f.required,
        };
        if (f.field_type === 'dropdown') {
          const opts = Array.isArray(f.options) ? f.options : (f.options || '').split(',');
          cleaned.options = opts.map(o => String(o).trim()).filter(Boolean);
        }
        if (f.field_type === 'date' && f.reminder_days_before !== null && f.reminder_days_before !== '' && f.reminder_days_before !== undefined) {
          const n = parseInt(f.reminder_days_before);
          if (!isNaN(n) && n >= 0) cleaned.reminder_days_before = n;
        }
        if (f.field_type === 'date' && f.requires_assignee) {
          cleaned.requires_assignee = true;
        }
        return cleaned;
      });
    const payload = { ...equipmentCategoryForm, custom_fields: cleanedFields };
    setSavingEquipmentCategory(true);
    try {
      if (selectedEquipmentCategory) {
        await axios.put(`${API}/assets/equipment-categories/${selectedEquipmentCategory.id}`, payload);
        toast.success('Category updated');
      } else {
        await axios.post(`${API}/assets/equipment-categories`, payload);
        toast.success('Category created');
      }
      setShowEquipmentCategoryDialog(false);
      fetchEquipmentCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    } finally {
      setSavingEquipmentCategory(false);
    }
  };

  const handleDeleteEquipmentCategory = async () => {
    setSavingEquipmentCategory(true);
    try {
      await axios.delete(`${API}/assets/equipment-categories/${selectedEquipmentCategory.id}`);
      toast.success('Category deleted');
      setShowEquipmentCategoryDeleteDialog(false);
      setSelectedEquipmentCategory(null);
      fetchEquipmentCategories();
    } catch (error) {
      toast.error('Failed to delete category');
    } finally {
      setSavingEquipmentCategory(false);
    }
  };

  const handleReorderEquipmentCategories = async (dragIndex, dropIndex) => {
    const newCategories = [...equipmentCategories];
    const [removed] = newCategories.splice(dragIndex, 1);
    newCategories.splice(dropIndex, 0, removed);
    
    // Optimistic update
    setEquipmentCategories(newCategories);
    
    // Send new order to backend
    try {
      await axios.put(`${API}/assets/equipment-categories/reorder`, newCategories.map(c => c.id));
      toast.success('Order saved');
    } catch (error) {
      toast.error('Failed to reorder');
      fetchEquipmentCategories(); // Revert on error
    }
  };

  const moveEquipmentCategory = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= equipmentCategories.length) return;
    handleReorderEquipmentCategories(index, newIndex);
  };

  const loading = loadingServices || loadingClassifications || loadingProposalTypes || loadingJobStatuses || loadingEquipmentCategories;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-32 bg-slate-200 animate-pulse rounded" />
        <div className="space-y-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-20 bg-slate-200 animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="services-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Barlow_Condensed'] tracking-tight">
            <Settings className="inline-block w-8 h-8 mr-2 text-amber-500" />
            Settings
          </h1>
          <p className="text-slate-500 mt-1">Manage services, classifications, and system options</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-1">
        <button
          onClick={() => setActiveTab('services')}
          className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
            activeTab === 'services' 
              ? 'text-amber-600 border-b-2 border-amber-500' 
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Wrench className="w-4 h-4 inline-block mr-2" />
          Services ({services.length})
        </button>
        <button
          onClick={() => setActiveTab('classifications')}
          className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
            activeTab === 'classifications' 
              ? 'text-amber-600 border-b-2 border-amber-500' 
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Building2 className="w-4 h-4 inline-block mr-2" />
          Industry Classifications ({classifications.length})
        </button>
        <button
          onClick={() => setActiveTab('proposalTypes')}
          className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
            activeTab === 'proposalTypes' 
              ? 'text-amber-600 border-b-2 border-amber-500' 
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <FileText className="w-4 h-4 inline-block mr-2" />
          Proposal Types ({proposalTypes.length})
        </button>
        <button
          onClick={() => setActiveTab('jobStatuses')}
          className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
            activeTab === 'jobStatuses' 
              ? 'text-amber-600 border-b-2 border-amber-500' 
              : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="job-statuses-tab"
        >
          <FileText className="w-4 h-4 inline-block mr-2" />
          Job Statuses ({jobStatuses.length})
        </button>
        <button
          onClick={() => setActiveTab('equipment-categories')}
          className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
            activeTab === 'equipment-categories' 
              ? 'text-amber-600 border-b-2 border-amber-500' 
              : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="equipment-categories-tab"
        >
          <Truck className="w-4 h-4 inline-block mr-2" />
          Equipment Categories ({equipmentCategories.length})
        </button>
        <button
          onClick={() => setActiveTab('eventTypes')}
          className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
            activeTab === 'eventTypes'
              ? 'text-amber-600 border-b-2 border-amber-500'
              : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="event-types-tab"
        >
          <Calendar className="w-4 h-4 inline-block mr-2" />
          Event Types ({eventTypes.filter(t => t.active !== false).length})
        </button>
        <button
          onClick={() => setActiveTab('export')}
          className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
            activeTab === 'export' 
              ? 'text-amber-600 border-b-2 border-amber-500' 
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Database className="w-4 h-4 inline-block mr-2" />
          Data Export
        </button>
        <button
          onClick={() => setActiveTab('email')}
          className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
            activeTab === 'email' 
              ? 'text-amber-600 border-b-2 border-amber-500' 
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Mail className="w-4 h-4 inline-block mr-2" />
          Email Notifications
        </button>
        <button
          onClick={() => setActiveTab('backup-docs')}
          className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
            activeTab === 'backup-docs' 
              ? 'text-amber-600 border-b-2 border-amber-500' 
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <FileText className="w-4 h-4 inline-block mr-2" />
          Backup Guide
        </button>
        <button
          onClick={() => setActiveTab('analytics')}
          className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
            activeTab === 'analytics' 
              ? 'text-amber-600 border-b-2 border-amber-500' 
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <BarChart3 className="w-4 h-4 inline-block mr-2" />
          Analytics
        </button>
        <button
          onClick={() => setActiveTab('maintenance-settings')}
          data-testid="approvers-tab"
          className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
            activeTab === 'maintenance-settings' 
              ? 'text-amber-600 border-b-2 border-amber-500' 
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Settings className="w-4 h-4 inline-block mr-2" />
          Approvers
        </button>
        <button
          onClick={() => setActiveTab('field-form-options')}
          className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
            activeTab === 'field-form-options'
              ? 'text-amber-600 border-b-2 border-amber-500'
              : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="field-form-options-tab"
        >
          <FileText className="w-4 h-4 inline-block mr-2" />
          Field Form Options
        </button>
        <button
          onClick={() => setActiveTab('job-edit-layout')}
          className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
            activeTab === 'job-edit-layout'
              ? 'text-amber-600 border-b-2 border-amber-500'
              : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="job-edit-layout-tab"
        >
          <Eye className="w-4 h-4 inline-block mr-2" />
          Job Edit Layout
        </button>
        {(isAdmin() || isSuperUser?.()) && (
          <button
            onClick={() => setActiveTab('checklist-templates')}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'checklist-templates'
                ? 'text-amber-600 border-b-2 border-amber-500'
                : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid="checklist-templates-tab"
          >
            <ClipboardList className="w-4 h-4 inline-block mr-2" />
            Form & Checklist Templates
          </button>
        )}
        <button
          onClick={() => setActiveTab('styleguide')}
          className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
            activeTab === 'styleguide' 
              ? 'text-amber-600 border-b-2 border-amber-500' 
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Palette className="w-4 h-4 inline-block mr-2" />
          Styleguide
        </button>
      </div>

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="space-y-6">
          {/* Period Selector */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <h2 className="text-lg font-semibold text-slate-900">User Activity Analytics</h2>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">Period:</span>
              <select
                value={analyticsPeriod}
                onChange={(e) => setAnalyticsPeriod(parseInt(e.target.value))}
                className="px-3 py-1.5 border border-slate-300 rounded-md text-sm bg-white"
              >
                <option value={7}>Last 7 days</option>
                <option value={14}>Last 14 days</option>
                <option value={30}>Last 30 days</option>
                <option value={60}>Last 60 days</option>
                <option value={90}>Last 90 days</option>
              </select>
              <Button variant="outline" size="sm" onClick={fetchAnalytics} disabled={loadingAnalytics}>
                {loadingAnalytics ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {loadingAnalytics ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
            </div>
          ) : analyticsData ? (
            <>
              {/* Overall Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Card className="border-slate-200">
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-amber-600">{analyticsData.overall_stats?.total_page_views || 0}</div>
                    <div className="text-sm text-slate-500 mt-1">Page Views</div>
                  </CardContent>
                </Card>
                <Card className="border-slate-200">
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-blue-600">{analyticsData.overall_stats?.total_actions || 0}</div>
                    <div className="text-sm text-slate-500 mt-1">Actions</div>
                  </CardContent>
                </Card>
                <Card className="border-slate-200">
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-green-600">{analyticsData.overall_stats?.total_unique_users || 0}</div>
                    <div className="text-sm text-slate-500 mt-1">Active Users</div>
                  </CardContent>
                </Card>
                <Card className="border-slate-200">
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-purple-600">{analyticsData.overall_stats?.total_events || 0}</div>
                    <div className="text-sm text-slate-500 mt-1">Total Events</div>
                  </CardContent>
                </Card>
              </div>

              {/* Top 5 Pages */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-amber-500" />
                    Top 5 Most Visited Pages
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analyticsData.top_pages && analyticsData.top_pages.length > 0 ? (
                    <div className="space-y-3">
                      {analyticsData.top_pages.map((page, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                              index === 0 ? 'bg-amber-500' : index === 1 ? 'bg-slate-400' : index === 2 ? 'bg-amber-700' : 'bg-slate-300'
                            }`}>
                              {index + 1}
                            </div>
                            <span className="font-medium">{page.page}</span>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-amber-600">{page.view_count} views</div>
                            <div className="text-xs text-slate-500">{page.unique_user_count} unique users</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-4">No page view data yet</p>
                  )}
                </CardContent>
              </Card>

              {/* User Activity Breakdown */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-500" />
                    User Activity Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analyticsData.user_activity && analyticsData.user_activity.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-3 py-2 text-left">User</th>
                            <th className="px-3 py-2 text-right">Page Views</th>
                            <th className="px-3 py-2 text-right">Actions</th>
                            <th className="px-3 py-2 text-right hidden sm:table-cell">Time (min)</th>
                            <th className="px-3 py-2 text-right hidden sm:table-cell">Pages</th>
                            <th className="px-3 py-2 text-right hidden md:table-cell">Last Active</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analyticsData.user_activity.map((user, index) => (
                            <tr key={index} className="border-t">
                              <td className="px-3 py-2">
                                <div className="font-medium">{user.user_name}</div>
                                <div className="text-xs text-slate-400 truncate max-w-[150px]">{user.user_email}</div>
                              </td>
                              <td className="px-3 py-2 text-right font-medium">{user.page_views}</td>
                              <td className="px-3 py-2 text-right">{user.actions}</td>
                              <td className="px-3 py-2 text-right hidden sm:table-cell">{Math.round(user.total_time_minutes || 0)}</td>
                              <td className="px-3 py-2 text-right hidden sm:table-cell">{user.unique_pages}</td>
                              <td className="px-3 py-2 text-right text-xs text-slate-500 hidden md:table-cell">
                                {user.last_activity ? new Date(user.last_activity).toLocaleDateString() : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-4">No user activity data yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Daily Activity Trend */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                    <Clock className="w-5 h-5 text-green-500" />
                    Activity Over Time
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analyticsData.daily_activity && analyticsData.daily_activity.length > 0 ? (
                    <div className="overflow-x-auto">
                      <div className="min-w-[500px]">
                        <div className="flex items-end gap-1 h-40">
                          {analyticsData.daily_activity.slice(-30).map((day, index) => {
                            const maxViews = Math.max(...analyticsData.daily_activity.map(d => d.page_views || 0), 1);
                            const height = ((day.page_views || 0) / maxViews) * 100;
                            return (
                              <div key={index} className="flex-1 flex flex-col items-center group">
                                <div 
                                  className="w-full bg-amber-500 rounded-t hover:bg-amber-400 transition-colors cursor-pointer"
                                  style={{ height: `${Math.max(height, 2)}%` }}
                                  title={`${day.date}: ${day.page_views} views, ${day.unique_user_count} users`}
                                />
                              </div>
                            );
                          })}
                        </div>
                        <div className="flex justify-between mt-2 text-xs text-slate-400">
                          <span>{analyticsData.daily_activity[0]?.date}</span>
                          <span>{analyticsData.daily_activity[analyticsData.daily_activity.length - 1]?.date}</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-4">No daily activity data yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Average Time Per Page */}
              {analyticsData.avg_time_per_page && analyticsData.avg_time_per_page.length > 0 && (
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                      <Clock className="w-5 h-5 text-purple-500" />
                      Average Time Spent Per Page
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {analyticsData.avg_time_per_page.map((page, index) => (
                        <div key={index} className="flex items-center justify-between p-2 hover:bg-slate-50 rounded">
                          <span className="font-medium">{page.page}</span>
                          <div className="text-right">
                            <span className="font-bold text-purple-600">
                              {Math.floor(page.avg_time_seconds / 60)}m {page.avg_time_seconds % 60}s
                            </span>
                            <span className="text-xs text-slate-400 ml-2">({page.total_visits} visits)</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Top Actions */}
              {analyticsData.top_actions && analyticsData.top_actions.length > 0 && (
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                      <MousePointer className="w-5 h-5 text-rose-500" />
                      Top User Actions
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {analyticsData.top_actions.map((action, index) => (
                        <div key={index} className="flex items-center justify-between p-2 hover:bg-slate-50 rounded">
                          <div>
                            <span className="font-medium">{action.action_name || 'Unknown'}</span>
                            <span className="text-xs text-slate-400 ml-2">on {action.page}</span>
                          </div>
                          <Badge variant="outline">{action.count} times</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card className="border-slate-200">
              <CardContent className="py-12 text-center">
                <BarChart3 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">No analytics data available yet.</p>
                <p className="text-sm text-slate-400 mt-1">Data will appear as users interact with the application.</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Backup Documentation Tab */}
      {activeTab === 'backup-docs' && (
        <div className="space-y-6">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                <Database className="w-5 h-5 text-amber-500" />
                FieldReady - Backup Strategy Guide
              </CardTitle>
              <p className="text-sm text-slate-500">Last Updated: February 8, 2026</p>
            </CardHeader>
            <CardContent className="prose prose-slate max-w-none">
              
              {/* Executive Summary */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold text-slate-900 mt-0">Executive Summary</h3>
                <p className="text-slate-700 mb-0">
                  FieldReady implements a comprehensive multi-layer backup strategy to protect all business data. 
                  Data is backed up across three separate systems: source code (GitHub), database (Cloudinary), 
                  and uploaded files (Cloudinary). This ensures business continuity and data recovery capabilities.
                </p>
              </div>

              {/* Overview Table */}
              <h3 className="text-lg font-semibold text-slate-900">Backup Components Overview</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm border border-slate-200 rounded-lg">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-4 py-2 text-left font-semibold">Component</th>
                      <th className="px-4 py-2 text-left font-semibold">Data Type</th>
                      <th className="px-4 py-2 text-left font-semibold">Location</th>
                      <th className="px-4 py-2 text-left font-semibold">Frequency</th>
                      <th className="px-4 py-2 text-left font-semibold">Retention</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-slate-200">
                      <td className="px-4 py-2 font-medium">📦 Source Code</td>
                      <td className="px-4 py-2">Application code</td>
                      <td className="px-4 py-2">GitHub</td>
                      <td className="px-4 py-2">On-demand</td>
                      <td className="px-4 py-2">Unlimited</td>
                    </tr>
                    <tr className="border-t border-slate-200 bg-slate-50">
                      <td className="px-4 py-2 font-medium">🗄️ Database</td>
                      <td className="px-4 py-2">All business records</td>
                      <td className="px-4 py-2">Cloudinary</td>
                      <td className="px-4 py-2">Weekly (auto)</td>
                      <td className="px-4 py-2">4 weeks</td>
                    </tr>
                    <tr className="border-t border-slate-200">
                      <td className="px-4 py-2 font-medium">📁 Files & Photos</td>
                      <td className="px-4 py-2">Receipts, photos, attachments</td>
                      <td className="px-4 py-2">Cloudinary</td>
                      <td className="px-4 py-2">Real-time</td>
                      <td className="px-4 py-2">Permanent</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Source Code Section */}
              <h3 className="text-lg font-semibold text-slate-900 mt-6">1. Source Code Backup</h3>
              <div className="bg-slate-50 rounded-lg p-4 mb-4">
                <p className="mb-2"><strong>Location:</strong> <a href="https://github.com/chanhoyle-ux/fieldflow-em" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">GitHub Repository</a></p>
                <p className="mb-2"><strong>What's Backed Up:</strong> Application source code, configuration files, documentation</p>
                <p className="mb-0"><strong>How to Backup:</strong> In Emergent platform, click "Save to GitHub"</p>
              </div>

              {/* Database Section */}
              <h3 className="text-lg font-semibold text-slate-900 mt-6">2. Database Backup (Automatic)</h3>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <p className="mb-2"><strong>Schedule:</strong> Every day at 2:00 AM</p>
                <p className="mb-2"><strong>Retention:</strong> 30 days (older backups auto-deleted)</p>
                <p className="mb-2"><strong>Location:</strong> Cloudinary (<code>fieldready/backups/</code> folder)</p>
                <p className="mb-0"><strong>Format:</strong> JSON file (<code>fieldready_backup_YYYYMMDD_HHMMSS.json</code>)</p>
              </div>
              <p className="text-slate-700">
                <strong>Collections Backed Up (25):</strong> contacts, people, opportunities, proposals, jobs, 
                timesheets, weekly_timesheets, expense_reports, precons, soft_digs, locates, time_events,
                tickets_811, equipment, maintenance_forms, services, industry_classifications, proposal_types,
                users, invites, password_resets, website_inquiries, recurring_jobs, settings, backup_logs
              </p>
              <p className="text-slate-700 text-sm mt-2">
                <em>Note: analytics_events and sync_logs are excluded from backup (non-critical data, stored in Atlas)</em>
              </p>
              <p className="text-slate-700">
                <strong>Manual Backup:</strong> Go to <em>Settings → Data Export</em> tab and click "Backup Now"
              </p>

              {/* Files Section */}
              <h3 className="text-lg font-semibold text-slate-900 mt-6">3. Files & Photos Backup</h3>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="mb-2"><strong>Location:</strong> <a href="https://console.cloudinary.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Cloudinary Dashboard</a></p>
                <p className="mb-2"><strong>Backup Frequency:</strong> Real-time (uploaded immediately)</p>
                <p className="mb-0"><strong>Retention:</strong> Permanent (until manually deleted)</p>
              </div>
              <p className="text-slate-700">
                <strong>Folders:</strong>
              </p>
              <ul className="text-slate-700">
                <li><code>fieldflow/jobs/files/</code> - Job attachments</li>
                <li><code>fieldflow/jobs/photos/</code> - Job photos</li>
                <li><code>fieldflow/proposals/</code> - Proposal attachments</li>
                <li><code>fieldflow/receipts/</code> - Expense receipts</li>
              </ul>

              {/* Verification Checklist */}
              <h3 className="text-lg font-semibold text-slate-900 mt-6">4. Backup Verification Checklist</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">Weekly Verification</h4>
                  <ul className="text-slate-700 text-sm space-y-1">
                    <li>☐ Check automatic backup ran on Sunday</li>
                    <li>☐ Verify backup file size is reasonable</li>
                    <li>☐ Confirm backup count shows expected number</li>
                  </ul>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">Monthly Verification</h4>
                  <ul className="text-slate-700 text-sm space-y-1">
                    <li>☐ Download a backup and verify it opens</li>
                    <li>☐ Spot-check data matches current records</li>
                    <li>☐ Ensure "Save to GitHub" has been run</li>
                  </ul>
                </div>
              </div>

              {/* Disaster Recovery */}
              <h3 className="text-lg font-semibold text-slate-900 mt-6">5. Disaster Recovery Scenarios</h3>
              <div className="space-y-3">
                <div className="border border-slate-200 rounded-lg p-3">
                  <h4 className="font-semibold text-slate-900">Accidental Data Deletion</h4>
                  <p className="text-sm text-slate-600 mb-0">Download appropriate backup from Data Export tab → Contact support for selective restoration</p>
                </div>
                <div className="border border-slate-200 rounded-lg p-3">
                  <h4 className="font-semibold text-slate-900">Application Error</h4>
                  <p className="text-sm text-slate-600 mb-0">Use Emergent's rollback feature → Database and files remain unaffected</p>
                </div>
                <div className="border border-slate-200 rounded-lg p-3">
                  <h4 className="font-semibold text-slate-900">Complete System Recovery</h4>
                  <p className="text-sm text-slate-600 mb-0">Restore code from GitHub → Restore database from Cloudinary backup → Redeploy application</p>
                </div>
              </div>

              {/* Quick Reference */}
              <h3 className="text-lg font-semibold text-slate-900 mt-6">6. Quick Reference</h3>
              <div className="bg-slate-800 text-slate-100 rounded-lg p-4 font-mono text-sm">
                <pre className="whitespace-pre-wrap mb-0">{`📦 SOURCE CODE
   Location: GitHub
   Action: Click "Save to GitHub" in Emergent

🗄️ DATABASE
   Location: Cloudinary (fieldready/backups/)
   Auto: Every day at 2:00 AM
   Retention: 30 days
   Collections: 20 (all business data)
   Manual: Settings → Data Export → Backup Now

📁 FILES & PHOTOS
   Location: Cloudinary
   Auto: Uploaded in real-time
   Access: console.cloudinary.com

🔗 IMPORTANT LINKS
   App: fieldready-1.preview.emergentagent.com
   GitHub: github.com/chanhoyle-ux/fieldflow-em
   Cloudinary: console.cloudinary.com`}</pre>
              </div>

              {/* Contact */}
              <h3 className="text-lg font-semibold text-slate-900 mt-6">7. Support Contacts</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">Emergent Support</h4>
                  <p className="text-sm text-slate-600 mb-1">Email: support@emergent.sh</p>
                  <p className="text-sm text-slate-600 mb-0">Discord: discord.gg/VzKfwCXC4A</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">Cloudinary Support</h4>
                  <p className="text-sm text-slate-600 mb-0">Website: cloudinary.com/contact</p>
                </div>
              </div>

            </CardContent>
          </Card>
        </div>
      )}

      {/* Event Types Tab */}
      {activeTab === 'eventTypes' && (
        <div className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg font-['Barlow_Condensed']">Calendar Event Types</CardTitle>
                <p className="text-sm text-slate-500 mt-1">Categories used when creating calendar events (PTO, Holidays, Training, etc.). Default types can be hidden but not deleted.</p>
              </div>
              <Button
                size="sm"
                onClick={openNewEventType}
                className="h-9 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
                data-testid="event-type-add-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Type
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {eventTypes.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-6">No event types yet.</p>
                )}
                {eventTypes.map(t => (
                  <div
                    key={t.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border ${t.active === false ? 'border-slate-200 bg-slate-50 opacity-60' : 'border-slate-200 bg-white'}`}
                    data-testid={`event-type-row-${t.id}`}
                  >
                    <span
                      className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide"
                      style={{ backgroundColor: t.color, color: t.text_color }}
                    >
                      {t.name}
                    </span>
                    <span className="text-xs text-slate-500 font-mono">{t.color}</span>
                    {t.is_default && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">DEFAULT</span>
                    )}
                    {t.active === false && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">HIDDEN</span>
                    )}
                    <div className="flex-1" />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleEventTypeActive(t)}
                      className="h-8 border-slate-200 text-slate-700 hover:bg-slate-50"
                      data-testid={`event-type-toggle-${t.id}`}
                    >
                      {t.active === false ? 'Re-enable' : 'Hide'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditEventType(t)}
                      className="h-8 border-slate-200 text-slate-700 hover:bg-slate-50"
                      data-testid={`event-type-edit-${t.id}`}
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteEventType(t)}
                      className="h-8 text-red-600 border-red-200 hover:bg-red-50"
                      data-testid={`event-type-delete-${t.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Event Type Dialog */}
      <Dialog open={showEventTypeDialog} onOpenChange={setShowEventTypeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-xl">
              {editingEventType ? 'Edit Event Type' : 'New Event Type'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="et-name">Name *</Label>
              <Input
                id="et-name"
                value={eventTypeForm.name}
                onChange={e => setEventTypeForm({ ...eventTypeForm, name: e.target.value })}
                placeholder="e.g. Field Day, Sick Leave"
                disabled={editingEventType?.is_default}
                data-testid="event-type-name-input"
              />
              {editingEventType?.is_default && (
                <p className="text-xs text-slate-500 mt-1">Default type names cannot be changed.</p>
              )}
            </div>
            <div>
              <Label htmlFor="et-color">Color</Label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  id="et-color"
                  type="color"
                  value={eventTypeForm.color}
                  onChange={e => setEventTypeForm({ ...eventTypeForm, color: e.target.value })}
                  className="w-12 h-10 rounded cursor-pointer border border-slate-200"
                  data-testid="event-type-color-input"
                />
                <Input
                  value={eventTypeForm.color}
                  onChange={e => setEventTypeForm({ ...eventTypeForm, color: e.target.value })}
                  placeholder="#dbeafe"
                  className="font-mono"
                />
                <div
                  className="px-3 py-1 rounded-full text-xs font-bold uppercase"
                  style={{
                    backgroundColor: eventTypeForm.color,
                    color: (() => {
                      try {
                        const h = eventTypeForm.color.replace('#','');
                        const r = parseInt(h.substr(0,2),16);
                        const g = parseInt(h.substr(2,2),16);
                        const b = parseInt(h.substr(4,2),16);
                        return ((r*299+g*587+b*114)/1000) >= 160 ? '#1e293b' : '#ffffff';
                      } catch { return '#1e293b'; }
                    })(),
                  }}
                >
                  {eventTypeForm.name || 'Preview'}
                </div>
              </div>
            </div>
            <div>
              <Label htmlFor="et-sort">Sort Order</Label>
              <Input
                id="et-sort"
                type="number"
                value={eventTypeForm.sort_order}
                onChange={e => setEventTypeForm({ ...eventTypeForm, sort_order: parseInt(e.target.value) || 99 })}
                className="w-32"
              />
              <p className="text-xs text-slate-500 mt-1">Lower numbers show first in the dropdown.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEventTypeDialog(false)}>Cancel</Button>
            <Button
              onClick={handleSaveEventType}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
              data-testid="event-type-save-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {editingEventType ? 'Save Changes' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Data Export Tab */}
      {activeTab === 'export' && (
        <div className="space-y-6">
          {/* Pre-Deployment Reminder Banner */}
          <Card className="border-2 border-red-300 bg-red-50">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-red-800 text-lg">Before You Deploy!</h3>
                  <p className="text-red-700 mt-1">
                    Always create a backup before deploying to production. When deploying on Emergent:
                  </p>
                  <ul className="list-disc list-inside text-red-700 mt-2 space-y-1 text-sm">
                    <li><strong>Choose "Update existing deployment"</strong> to keep your data</li>
                    <li><strong>Avoid "Fresh deployment"</strong> as it will wipe your database</li>
                  </ul>
                  <div className="mt-3">
                    <Button 
                      onClick={handleTriggerBackup}
                      disabled={exporting}
                      className="bg-red-600 hover:bg-red-700 text-white font-bold"
                    >
                      {exporting ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Database className="w-4 h-4 mr-2" />
                      )}
                      Backup Now Before Deploying
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* OneDrive Storage Connection */}
          <Card className={`border-2 ${onedriveStatus?.connected ? 'border-green-300 bg-green-50' : 'border-blue-300 bg-blue-50'}`}>
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <Database className={`w-6 h-6 flex-shrink-0 mt-0.5 ${onedriveStatus?.connected ? 'text-green-600' : 'text-blue-500'}`} />
                <div className="flex-1">
                  <h3 className={`font-bold text-lg ${onedriveStatus?.connected ? 'text-green-800' : 'text-blue-800'}`}>
                    Microsoft OneDrive Storage
                  </h3>
                  {onedriveStatus?.connected ? (
                    <div>
                      <p className="text-green-700 mt-1">
                        Connected as <strong>{onedriveStatus.user_email}</strong>
                      </p>
                      <p className="text-green-600 text-sm mt-1">
                        All new file uploads and backups will be saved to your OneDrive under /FieldReady/
                      </p>
                      <div className="mt-3">
                        <Button 
                          variant="outline"
                          size="sm"
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          onClick={handleDisconnectOnedrive}
                          data-testid="disconnect-onedrive-btn"
                        >
                          Disconnect OneDrive
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-blue-700 mt-1">
                        Connect your OneDrive to store all files, photos, and backups in organized folders.
                      </p>
                      <p className="text-blue-600 text-sm mt-1">
                        Files will be organized under /FieldReady/ with subfolders for Jobs, Maintenance, Proposals, and Backups.
                      </p>
                      <div className="mt-3">
                        <Button 
                          className="bg-blue-600 hover:bg-blue-700 text-white font-bold"
                          onClick={handleConnectOnedrive}
                          data-testid="connect-onedrive-btn"
                        >
                          <Database className="w-4 h-4 mr-2" />
                          Connect OneDrive
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cloudinary Fallback Monitor - only show when OneDrive is connected */}
          {onedriveStatus?.connected && (
            <Card className="border-2 border-amber-200 bg-amber-50/50">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 flex-shrink-0 mt-0.5 text-amber-600" />
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-amber-800">Cloudinary Fallback Monitor</h3>
                    <p className="text-amber-700 text-sm mt-1">
                      Check for files that landed on Cloudinary instead of OneDrive, and migrate them.
                    </p>

                    {cloudinaryScan ? (
                      <div className="mt-3 bg-white rounded-lg p-3 border border-amber-200">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-2xl font-bold ${cloudinaryScan.total_cloudinary_files > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                            {cloudinaryScan.total_cloudinary_files}
                          </span>
                          <span className="text-slate-600 text-sm">files on Cloudinary</span>
                        </div>
                        {cloudinaryScan.total_cloudinary_files > 0 && (
                          <>
                            <div className="text-xs text-slate-500 space-y-0.5 mb-3">
                              {Object.entries(cloudinaryScan.breakdown || {}).map(([key, count]) => (
                                <div key={key}>{key}: <strong>{count}</strong></div>
                              ))}
                            </div>
                            <Button
                              size="sm"
                              className="bg-amber-600 hover:bg-amber-700 text-white"
                              onClick={handleMigrateToOnedrive}
                              disabled={migrating}
                              data-testid="migrate-to-onedrive-btn"
                            >
                              {migrating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Migrating...</> : 'Move All to OneDrive'}
                            </Button>
                            {cloudinaryScan.migration_errors?.length > 0 && (
                              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 max-h-32 overflow-y-auto">
                                <strong>Failed files:</strong>
                                {cloudinaryScan.migration_errors.map((err, i) => (
                                  <div key={i} className="mt-1 font-mono">{err}</div>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                        {cloudinaryScan.total_cloudinary_files === 0 && (
                          <p className="text-green-600 text-sm flex items-center gap-1 mb-2">
                            <CheckCircle className="w-4 h-4" /> All files are on OneDrive
                          </p>
                        )}
                        <div className="mt-3 pt-3 border-t border-amber-200">
                          <div className="flex flex-wrap gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-slate-300 text-slate-600 hover:bg-slate-100"
                              onClick={handleFixExtensions}
                              disabled={fixingNames}
                              data-testid="fix-extensions-btn"
                            >
                              {fixingNames ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Fixing...</> : 'Fix File Names'}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-slate-300 text-slate-600 hover:bg-slate-100"
                              onClick={handleFixUrls}
                              disabled={fixingUrls}
                              data-testid="fix-urls-btn"
                            >
                              {fixingUrls ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Fixing...</> : 'Fix File URLs'}
                            </Button>
                          </div>
                          <p className="text-xs text-slate-400 mt-1">Fix file names for OneDrive preview, or fix URLs so files open in-browser</p>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3">
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-amber-400 text-amber-700 hover:bg-amber-100"
                          onClick={handleScanCloudinary}
                          disabled={scanningCloudinary}
                          data-testid="scan-cloudinary-btn"
                        >
                          {scanningCloudinary ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Scanning...</> : 'Scan for Cloudinary Files'}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                <Download className="w-5 h-5 text-amber-500" />
                Export All Data
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 mb-4">
                Use the <strong>Download Backup</strong> button under All Backup Locations
                → Database (below) for a complete backup. That path streams every part
                directly from OneDrive to your computer with no server-side memory
                bottleneck — the same file(s) your nightly backup produces.
              </p>
              <p className="text-xs text-slate-500">
                Retired 2026-08-13: the old &quot;Download Full Backup (JSON)&quot; button on this card
                was a legacy endpoint that only covered 12 collections and timed out on
                production-size data. Second clicks could freeze the browser while the
                first response was still being buffered.
              </p>
            </CardContent>
          </Card>

          {/* Automatic Backups Card */}
          <Card className="border-slate-200 border-2 border-green-200 bg-green-50">
            <CardHeader>
              <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                <Database className="w-5 h-5 text-green-600" />
                Automatic Daily Backups
                <span className="ml-2 px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                  ENABLED
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 mb-4">
                Your data is automatically backed up{onedriveStatus?.connected ? ' to OneDrive' : ' to Cloudinary'} every day at 2:00 AM.
                Backups are retained indefinitely{onedriveStatus?.connected ? ' on OneDrive' : ''}. All {backupStatus?.collections_backed_up || 20} collections are included.
              </p>
              
              {loadingBackupStatus ? (
                <div className="flex items-center gap-2 text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading backup status...
                </div>
              ) : backupStatus ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 p-3 bg-white rounded-lg border border-green-200">
                    <div>
                      <p className="text-xs text-slate-500">Schedule</p>
                      <p className="font-medium text-slate-900">{backupStatus.schedule}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Retention</p>
                      <p className="font-medium text-slate-900" data-testid="backup-retention-label">{backupStatus.retention || 'Indefinite'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Next Backup</p>
                      <p className="font-medium text-slate-900">
                        {backupStatus.next_backup 
                          ? new Date(backupStatus.next_backup).toLocaleString()
                          : 'Scheduled'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Recent Backups</p>
                      <p className="font-medium text-slate-900">{backupStatus.recent_backups?.length || 0} saved</p>
                    </div>
                  </div>
                  
                  {backupStatus.recent_backups?.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-2">Recent Backups:</p>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {backupStatus.recent_backups.map((backup, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-white rounded border border-slate-200 text-sm">
                            <div className="flex-1">
                              <span className="font-medium">{backup.filename}</span>
                              <div className="text-xs text-slate-500 mt-1">
                                {(backup.size_bytes / 1024).toFixed(1)} KB • {backup.total_records} records • {new Date(backup.created_at).toLocaleString()}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 ml-2">
                              {backup.cloudinary_url && (
                                <>
                                  <a
                                    href={backup.cloudinary_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 text-xs font-medium px-2 py-1 bg-blue-50 rounded"
                                  >
                                    Download
                                  </a>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleRestoreClick(backup)}
                                    disabled={restoring}
                                    className="text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                                  >
                                    Restore
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      onClick={handleTriggerBackup}
                      disabled={exporting}
                      variant="outline"
                      className="border-green-300 text-green-700 hover:bg-green-100"
                      data-testid="backup-now-btn"
                    >
                      {exporting ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Database className="w-4 h-4 mr-2" />
                      )}
                      Backup Now (to {onedriveStatus?.connected ? 'OneDrive' : 'Cloudinary'})
                    </Button>

                    {/* Local Upload Restore — bypasses OneDrive URL expiry. */}
                    <label
                      className="inline-flex items-center px-3 py-2 rounded-md border border-amber-300 text-amber-700 hover:bg-amber-50 cursor-pointer text-sm font-medium"
                      data-testid="restore-local-upload-label"
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Restore from Local Files
                      <input
                        type="file"
                        accept=".json,application/json"
                        multiple
                        onChange={handleLocalUploadSelect}
                        className="hidden"
                        data-testid="restore-local-upload-input"
                      />
                    </label>
                  </div>
                  <p className="text-xs text-slate-500">
                    Local Upload restores work with backups of any age (OneDrive download links expire after 1 hour).
                    Select every part file (<span className="font-mono">*.json</span>) of the backup you want to restore.
                  </p>
                </div>
              ) : (
                <p className="text-slate-500">Unable to load backup status</p>
              )}
            </CardContent>
          </Card>

          {/* Weekly Full-Content Export Card (2026-09-07, paused 2026-09-29) */}
          <Card className="border-slate-200 border-2 border-indigo-200 bg-indigo-50">
            <CardHeader>
              <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                <Download className="w-5 h-5 text-indigo-600" />
                Weekly Full-Content Export
                {weeklyExportStatus?.schedule_enabled === false ? (
                  <span className="ml-2 px-2 py-1 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full" data-testid="weekly-export-paused-badge">
                    PAUSED
                  </span>
                ) : (
                  <span className="ml-2 px-2 py-1 bg-indigo-100 text-indigo-700 text-xs font-semibold rounded-full">
                    ENABLED
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 mb-4">
                Every Sunday at 3:00 AM, a zip of the actual file content (photos, signatures, PDFs — not
                just database records) currently in the live OneDrive folder is built and uploaded to a
                separate, dedicated "backup destination" OneDrive account. Read-only against the live
                folder — nothing there is ever deleted, moved, or modified. The last 12 weekly exports are kept.
              </p>

              {weeklyExportStatus?.schedule_enabled === false && (
                <div className="p-3 rounded-lg border border-amber-200 bg-amber-50 mb-4" data-testid="weekly-export-paused-note">
                  <p className="text-amber-800 text-sm">
                    {weeklyExportStatus.paused_note || 'Automatic weekly export is paused.'}
                  </p>
                </div>
              )}

              {/* Backup destination connection */}
              <div className={`p-3 rounded-lg border mb-4 ${backupDestStatus?.connected ? 'bg-white border-green-200' : 'bg-white border-indigo-200'}`}>
                {backupDestStatus?.connected ? (
                  <div>
                    <p className="text-green-700 text-sm">
                      Backup destination connected as <strong>{backupDestStatus.user_email}</strong>
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2 text-red-600 border-red-300 hover:bg-red-50"
                      onClick={handleDisconnectBackupDest}
                      data-testid="disconnect-backup-dest-btn"
                    >
                      Disconnect Backup Destination
                    </Button>
                  </div>
                ) : (
                  <div>
                    <p className="text-slate-600 text-sm mb-2">
                      Connect the separate OneDrive account that should receive each week's export
                      (e.g. your personal OneDrive).
                    </p>
                    <Button
                      size="sm"
                      className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold"
                      onClick={handleConnectBackupDest}
                      data-testid="connect-backup-dest-btn"
                    >
                      <Database className="w-4 h-4 mr-2" />
                      Connect Backup Destination
                    </Button>
                  </div>
                )}
              </div>

              {loadingWeeklyExportStatus ? (
                <div className="flex items-center gap-2 text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading export status...
                </div>
              ) : weeklyExportStatus ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 p-3 bg-white rounded-lg border border-indigo-200">
                    <div>
                      <p className="text-xs text-slate-500">Schedule</p>
                      <p className="font-medium text-slate-900">{weeklyExportStatus.schedule}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Retention</p>
                      <p className="font-medium text-slate-900" data-testid="weekly-export-retention-label">{weeklyExportStatus.retention}</p>
                    </div>
                  </div>

                  {weeklyExportStatus.recent_exports?.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-2">Recent Exports:</p>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {weeklyExportStatus.recent_exports.map((exp) => (
                          <div key={exp.id} className="flex items-center justify-between p-3 bg-white rounded border border-slate-200 text-sm" data-testid={`weekly-export-row-${exp.id}`}>
                            <div className="flex-1">
                              <span className="font-medium">{exp.filename || exp.id}</span>
                              <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                                exp.status === 'success' ? 'bg-green-100 text-green-700' :
                                exp.status === 'failed' ? 'bg-red-100 text-red-700' :
                                exp.status === 'interrupted' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'
                              }`}>
                                {exp.status}
                              </span>
                              <div className="text-xs text-slate-500 mt-1">
                                {exp.size_bytes ? `${formatBytes(exp.size_bytes)} • ` : ''}
                                {exp.file_count ? `${exp.file_count} files • ` : ''}
                                {new Date(exp.created_at).toLocaleString()}
                              </div>
                              {(exp.status === 'failed' || exp.status === 'interrupted') && exp.error && (
                                <div className="text-xs text-red-600 mt-1">{exp.error}</div>
                              )}
                              {exp.note && (
                                <div className="text-xs text-amber-600 mt-1">{exp.note}</div>
                              )}
                            </div>
                            {exp.status === 'success' && exp.object_storage_path && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="ml-2 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                                onClick={() => handleDownloadWeeklyExport(exp.id)}
                                data-testid={`download-weekly-export-${exp.id}`}
                              >
                                <Download className="w-3 h-3 mr-1" />
                                Download
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={handleTriggerWeeklyExport}
                    disabled={triggeringWeeklyExport}
                    variant="outline"
                    className="border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                    data-testid="trigger-weekly-export-btn"
                  >
                    {triggeringWeeklyExport ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Run Export Now
                  </Button>
                  <Button
                    onClick={fetchWeeklyExportStatus}
                    variant="ghost"
                    size="sm"
                    className="ml-2 text-slate-500"
                    data-testid="refresh-weekly-export-status-btn"
                  >
                    Refresh Status
                  </Button>
                </div>
              ) : (
                <p className="text-slate-500">Unable to load export status</p>
              )}
            </CardContent>
          </Card>

          {/* Restore Data Card */}
          {restoring && (
            <Card className="border-slate-200 border-2 border-amber-200 bg-amber-50">
              <CardContent className="py-6">
                <div className="flex items-center gap-3">
                  <Loader2 className="w-6 h-6 animate-spin text-amber-600" />
                  <div>
                    <p className="font-medium text-amber-800">Restoring data...</p>
                    <p className="text-sm text-amber-600">Please wait while your data is being restored.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Restore Result Card */}
          {restoreResult && (
            <Card className="border-slate-200 border-2 border-green-200 bg-green-50">
              <CardHeader>
                <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2 text-green-800">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Restore Complete
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-green-700 mb-3">
                  Restored from backup dated: {restoreResult.backup_date ? new Date(restoreResult.backup_date).toLocaleString() : 'Unknown'}
                </p>
                <div className="space-y-2">
                  {Object.entries(restoreResult.collections || {}).map(([name, stats]) => (
                    <div key={name} className="flex justify-between items-center p-2 bg-white rounded border border-green-200 text-sm">
                      <span className="font-medium text-slate-700 capitalize">{name.replace('_', ' ')}</span>
                      <span className="text-green-700">
                        +{stats.inserted} added
                        {stats.skipped_duplicates > 0 && (
                          <span className="text-slate-500 ml-2">({stats.skipped_duplicates} skipped)</span>
                        )}
                      </span>
                    </div>
                  ))}
                  {restoreResult.skipped?.length > 0 && (
                    <div className="mt-2 text-xs text-slate-500">
                      Skipped: {restoreResult.skipped.join(', ')}
                    </div>
                  )}
                </div>
                <Button 
                  onClick={() => setRestoreResult(null)} 
                  variant="outline" 
                  size="sm" 
                  className="mt-4"
                >
                  Dismiss
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Restore Reconciliation Card — post-mortem 2026-08-13
              Owner-only tool to identify docs inserted by the buggy
              Local Upload Restore run (before commit ab07cdd9). */}
          {isSuperUser && (
            <Card className="border-slate-200 border-2 border-purple-200 bg-purple-50" data-testid="reconcile-restore-card">
              <CardHeader>
                <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-purple-600" />
                  Restore Reconciliation (Owner Only)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-700 mb-3">
                  Cross-references backup file(s) against the live DB to find docs
                  inserted by the buggy Local Upload Restore. Select every part
                  file (<span className="font-mono">*.json</span>) — the endpoint
                  merges them the same way the restore does. READ-ONLY.
                </p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                      Approximate UTC time of the buggy restore (ISO-8601)
                    </label>
                    <input
                      type="text"
                      value={reconcileCutoff}
                      onChange={(e) => setReconcileCutoff(e.target.value)}
                      placeholder="2026-08-13T02:00:00+00:00"
                      className="w-full px-2 py-1 border border-slate-300 rounded text-sm font-mono"
                      data-testid="reconcile-cutoff-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Every doc with an ObjectId generated at or after this time will be flagged.
                    </p>
                  </div>
                  <label
                    className="inline-flex items-center px-3 py-2 rounded-md border border-purple-300 text-purple-700 hover:bg-purple-100 cursor-pointer text-sm font-medium"
                    data-testid="reconcile-upload-label"
                  >
                    {reconcileRunning ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Database className="w-4 h-4 mr-2" />
                    )}
                    {reconcileRunning ? 'Scanning...' : 'Upload Backup Files & Scan'}
                    <input
                      type="file"
                      accept=".json,application/json"
                      multiple
                      onChange={handleReconcileRestore}
                      className="hidden"
                      disabled={reconcileRunning}
                      data-testid="reconcile-upload-input"
                    />
                  </label>
                </div>

                {reconcileReport && (
                  <div className="mt-4 p-3 bg-white border border-purple-200 rounded-lg" data-testid="reconcile-report">
                    <div className="text-xs text-slate-500 mb-2 font-mono" data-testid="reconcile-merge-info">
                      Merged {reconcileReport.files_parsed}/{reconcileReport.files_uploaded} file(s) →{' '}
                      {reconcileReport.merged_collection_count} collection(s) in backup
                      {reconcileReport.parse_errors && reconcileReport.parse_errors.length > 0 && (
                        <span className="text-red-600 ml-2">
                          ⚠ {reconcileReport.parse_errors.length} parse error(s): {reconcileReport.parse_errors.join('; ')}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
                      <div>
                        <span className="text-xs text-slate-500">Suspect docs total</span>
                        <p className="font-bold text-purple-700" data-testid="reconcile-suspect-total">
                          {reconcileReport.totals.suspect_docs_total}
                        </p>
                      </div>
                      <div>
                        <span className="text-xs text-slate-500">Pre-existing docs</span>
                        <p className="font-bold text-slate-700">
                          {reconcileReport.totals.preexisting_docs_total}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-1 max-h-64 overflow-y-auto text-xs font-mono">
                      {Object.entries(reconcileReport.collections)
                        .filter(([, m]) => m.suspect_count > 0)
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([name, meta]) => (
                          <div key={name} className="border-b border-purple-100 pb-2 mb-2">
                            <div className="font-semibold text-purple-800">
                              {name} — {meta.suspect_count} suspect / {meta.present_in_db_count} in DB / {meta.backup_id_count} in backup
                            </div>
                            {meta.suspect_docs.slice(0, 20).map((s) => (
                              <div key={s._id} className="text-slate-600 ml-2" data-testid={`reconcile-suspect-${s.id}`}>
                                id={s.id} _id={s._id.slice(0, 8)}... gen_time={s.objectid_generation_time}
                              </div>
                            ))}
                            {meta.suspect_docs.length > 20 && (
                              <div className="text-slate-400 ml-2 italic">... and {meta.suspect_docs.length - 20} more</div>
                            )}
                          </div>
                        ))}
                    </div>
                    <Button
                      onClick={() => {
                        const blob = new Blob([JSON.stringify(reconcileReport, null, 2)], { type: 'application/json' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `reconcile_report_${Date.now()}.json`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                      size="sm"
                      variant="outline"
                      className="mt-3"
                      data-testid="reconcile-download-report"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download Full JSON Report
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}



          {/* Dedupe Collection Card — post-mortem 2026-08-13
              Cleans up existing duplicates in the 9 natural-key
              collections that the pre-fix restore multiplied. */}
          {isSuperUser && (
            <Card className="border-slate-200 border-2 border-rose-200 bg-rose-50" data-testid="dedupe-collection-card">
              <CardHeader>
                <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-rose-600" />
                  Dedupe Natural-Key Collections (Owner Only)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-700 mb-3">
                  9 collections in the backup have no <code className="bg-white px-1">id</code> field
                  (e.g. <code className="bg-white px-1">blocked_emails</code>, <code className="bg-white px-1">settings</code>),
                  so pre-fix restores duplicated their rows. Group by natural key
                  → keep oldest ObjectId per group → delete the rest. Preview first,
                  then apply.
                </p>
                <div className="flex gap-2 items-center mb-3">
                  <select
                    value={dedupeCollection}
                    onChange={(e) => { setDedupeCollection(e.target.value); setDedupeReport(null); }}
                    className="px-2 py-1 border border-slate-300 rounded text-sm bg-white"
                    data-testid="dedupe-collection-select"
                  >
                    <option value="">— Select collection —</option>
                    {dedupeCollections.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  <Button
                    onClick={() => runDedupe(false)}
                    disabled={dedupeRunning || !dedupeCollection}
                    size="sm"
                    variant="outline"
                    className="border-rose-300 text-rose-700 hover:bg-rose-100"
                    data-testid="dedupe-preview-btn"
                  >
                    {dedupeRunning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                    Preview Duplicates
                  </Button>
                  {dedupeReport && dedupeReport.duplicate_count > 0 && (
                    <Button
                      onClick={() => runDedupe(true)}
                      disabled={dedupeRunning}
                      size="sm"
                      className="bg-rose-600 text-white hover:bg-rose-700"
                      data-testid="dedupe-apply-btn"
                    >
                      {dedupeRunning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                      Apply — Delete {dedupeReport.duplicate_count} row(s)
                    </Button>
                  )}
                </div>

                {dedupeReport && (
                  <div className="mt-3 p-3 bg-white border border-rose-200 rounded-lg text-xs" data-testid="dedupe-report">
                    <div className="mb-2 font-mono text-slate-700">
                      collection={dedupeReport.collection} natural_key={JSON.stringify(dedupeReport.natural_key)}
                    </div>
                    <div className="mb-2">
                      <span className="text-slate-500">Groups scanned:</span> {dedupeReport.group_count} •{' '}
                      <span className="text-slate-500">Groups with duplicates:</span> {dedupeReport.duplicate_group_count} •{' '}
                      <span className="text-slate-500">Duplicate rows:</span>{' '}
                      <span className="font-bold text-rose-700" data-testid="dedupe-duplicate-count">
                        {dedupeReport.duplicate_count}
                      </span>
                      {dedupeReport.deleted !== null && (
                        <span className="ml-2 text-green-700 font-bold" data-testid="dedupe-deleted-count">
                          Deleted: {dedupeReport.deleted}
                        </span>
                      )}
                    </div>
                    <div className="space-y-2 max-h-64 overflow-y-auto font-mono">
                      {dedupeReport.duplicates.map((g, i) => (
                        <div key={i} className="border-b border-rose-100 pb-2">
                          <div className="font-semibold text-rose-800 truncate">
                            natural_key={g.natural_key}
                          </div>
                          <div className="text-green-700 ml-2">
                            KEEP _id={g.keep._id.slice(0, 8)}... gen_time={g.keep.gen_time}
                          </div>
                          {g.delete.map(d => (
                            <div key={d._id} className="text-rose-600 ml-2">
                              {dedupeReport.dry_run ? 'WOULD-DEL' : 'DELETED'} _id={d._id.slice(0, 8)}... gen_time={d.gen_time}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                    {dedupeReport.duplicate_count === 0 && (
                      <div className="text-green-700">✓ No duplicates found in this collection.</div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}


          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                <Database className="w-5 h-5 text-amber-500" />
                Export Individual Collections
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 mb-4">
                Export specific data collections in JSON or CSV format.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'contacts', label: 'CRM / Contacts' },
                  { name: 'opportunities', label: 'Opportunities' },
                  { name: 'proposals', label: 'Proposals' },
                  { name: 'jobs', label: 'Jobs' },
                  { name: 'timesheets', label: 'Timesheets' },
                  { name: 'expense_reports', label: 'Expense Reports' },
                  { name: 'users', label: 'Team / Users' },
                  { name: 'website_inquiries', label: 'Website Inquiries' },
                ].map(collection => (
                  <div key={collection.name} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="font-medium text-slate-700">{collection.label}</span>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleExportCollection(collection.name, 'json')}
                        disabled={exporting}
                      >
                        JSON
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleExportCollection(collection.name, 'csv')}
                        disabled={exporting}
                      >
                        CSV
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 bg-blue-50">
            <CardContent className="py-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> File attachments and photos are stored in Cloudinary and can be accessed 
                through your Cloudinary dashboard at <a href="https://console.cloudinary.com" target="_blank" rel="noopener noreferrer" className="underline">console.cloudinary.com</a>
              </p>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                <Database className="w-5 h-5 text-amber-500" />
                All Backup Locations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 mb-4">
                Your FieldReady data is backed up in multiple locations for safety:
              </p>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div>
                    <h4 className="font-semibold text-slate-900">📦 Source Code</h4>
                    <p className="text-sm text-slate-600">Application code and configuration</p>
                  </div>
                  <a 
                    href="https://github.com/chanhoyle-ux/fieldflow-em" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors text-sm font-medium"
                  >
                    Open GitHub →
                  </a>
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div>
                    <h4 className="font-semibold text-slate-900">🗄️ Database</h4>
                    <p className="text-sm text-slate-600">Every collection, every part — downloaded from OneDrive directly to your computer.</p>
                    {fullBackupProgress && (
                      <p className="text-xs text-blue-700 mt-1 font-mono" data-testid="full-backup-progress">
                        Downloading part {fullBackupProgress.current} of {fullBackupProgress.total}
                        {fullBackupProgress.filename && `: ${fullBackupProgress.filename}`}
                      </p>
                    )}
                  </div>
                  <Button 
                    onClick={handleDownloadFullBackup}
                    disabled={fullBackupProgress !== null}
                    variant="outline"
                    className="text-sm"
                    data-testid="download-full-backup-btn"
                  >
                    {fullBackupProgress ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                    Download Backup
                  </Button>
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div>
                    <h4 className="font-semibold text-slate-900">📁 Files & Photos</h4>
                    <p className="text-sm text-slate-600">Receipts, job photos, attachments</p>
                  </div>
                  <a 
                    href="https://console.cloudinary.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-colors text-sm font-medium"
                  >
                    Open Cloudinary →
                  </a>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-4">
                💡 Tip: Use "Save to GitHub" in Emergent regularly to keep your code backup current.
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Email Notifications Tab */}
      {activeTab === 'email' && (
        <div className="space-y-6">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                <Mail className="w-5 h-5 text-amber-500" />
                Email Notification Settings
              </CardTitle>
              <p className="text-sm text-slate-500">
                Control which email notifications are sent by FieldReady
              </p>
            </CardHeader>
            <CardContent>
              {loadingEmailSettings ? (
                <div className="flex items-center gap-2 text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading settings...
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Team Invite Emails */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div>
                      <h4 className="font-medium text-slate-900">Team Invite Emails</h4>
                      <p className="text-sm text-slate-500 mt-1">
                        Send email with invite code when inviting new team members
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={emailSettings.team_invite_emails}
                        onChange={() => handleEmailSettingToggle('team_invite_emails')}
                        disabled={savingEmailSettings}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                    </label>
                  </div>

                  {/* Job Assignment Emails */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div>
                      <h4 className="font-medium text-slate-900">Job Assignment Emails</h4>
                      <p className="text-sm text-slate-500 mt-1">
                        Notify team members when they are assigned to a new job
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={emailSettings.job_assignment_emails}
                        onChange={() => handleEmailSettingToggle('job_assignment_emails')}
                        disabled={savingEmailSettings}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                    </label>
                  </div>

                  {/* Job Status Change Emails */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div>
                      <h4 className="font-medium text-slate-900">Job Status Change Emails</h4>
                      <p className="text-sm text-slate-500 mt-1">
                        Notify assigned team members when job status changes
                      </p>
                      <p className="text-xs text-amber-600 mt-1">
                        ⚠️ May generate many emails for busy teams
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={emailSettings.job_status_change_emails}
                        onChange={() => handleEmailSettingToggle('job_status_change_emails')}
                        disabled={savingEmailSettings}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                    </label>
                  </div>

                  {savingEmailSettings && (
                    <div className="flex items-center gap-2 text-amber-600 text-sm">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Push Notifications Card */}
          <Card className="border-slate-200 border-2 border-green-200 bg-green-50">
            <CardHeader>
              <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                <Bell className="w-5 h-5 text-green-600" />
                Push Notifications
                <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full ml-2">FREE</span>
              </CardTitle>
              <p className="text-sm text-green-700">
                Instant alerts on your phone - works like text messages but costs nothing
              </p>
            </CardHeader>
            <CardContent>
              {!pushSupported ? (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800">
                  <p className="font-medium">Push notifications not supported</p>
                  <p className="text-sm mt-1">Your browser doesn't support push notifications. Try using Chrome, Firefox, or Safari.</p>
                </div>
              ) : pushSubscribed ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-white rounded-lg border border-green-200">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                    <div>
                      <p className="font-medium text-green-800">Push notifications enabled!</p>
                      <p className="text-sm text-green-600">You'll receive instant alerts for job assignments.</p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={testPushNotification}
                      variant="outline"
                      className="w-full border-green-300 text-green-700 hover:bg-green-100"
                    >
                      <Bell className="w-4 h-4 mr-2" />
                      Send Test Notification
                    </Button>
                    <Button
                      onClick={unsubscribeFromPush}
                      variant="outline"
                      disabled={subscribingPush}
                      className="w-full border-red-300 text-red-600 hover:bg-red-50"
                    >
                      {subscribingPush ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <BellOff className="w-4 h-4 mr-2" />
                      )}
                      Disable Notifications
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-white rounded-lg border border-slate-200">
                    <Smartphone className="w-6 h-6 text-slate-400" />
                    <div>
                      <p className="font-medium text-slate-700">Enable push notifications</p>
                      <p className="text-sm text-slate-500">Get instant alerts when you're assigned to a job - even when the app is closed.</p>
                    </div>
                  </div>
                  
                  {pushPermission === 'denied' ? (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
                      <p className="font-medium">Notifications blocked</p>
                      <p className="text-sm mt-1">You've blocked notifications for this site. To enable them, click the lock icon in your browser's address bar and allow notifications.</p>
                    </div>
                  ) : (
                    <Button
                      onClick={subscribeToPush}
                      disabled={subscribingPush}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      {subscribingPush ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Bell className="w-4 h-4 mr-2" />
                      )}
                      Enable Push Notifications
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Email Info Card */}
          <Card className="border-slate-200 bg-blue-50 border-blue-200">
            <CardContent className="py-4">
              <h4 className="font-medium text-blue-900 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                About Email Notifications
              </h4>
              <ul className="text-sm text-blue-800 mt-2 space-y-1">
                <li>• Emails are sent from <code className="bg-blue-100 px-1 rounded">onboarding@resend.dev</code></li>
                <li>• To use your own domain, verify it in your Resend dashboard</li>
                <li>• Free tier: 100 emails/day (3,000/month)</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Services Tab */}
      {activeTab === 'services' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button 
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
              onClick={handleAddService}
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Service
            </Button>
          </div>

          {services.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="py-12 text-center">
                <Wrench className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-medium text-slate-900 mb-1">No services</h3>
                <p className="text-slate-500">Add your first service to get started</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {services.map(service => (
                <Card key={service.id} className="border-slate-200 hover:border-amber-300 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Wrench className="w-5 h-5 text-amber-600" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-semibold text-slate-900 truncate">{service.name}</h3>
                          {service.description && (
                            <p className="text-sm text-slate-500 line-clamp-2 mt-1">{service.description}</p>
                          )}
                          {/* Pricing Display */}
                          {(service.day_price || service.hourly_price || service.fixed_price || service.per_hole_price || service.night_price || service.linear_footage_price) && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {service.day_price && (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                                  ${Number(service.day_price).toLocaleString()}/day
                                </span>
                              )}
                              {service.hourly_price && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                                  ${Number(service.hourly_price).toLocaleString()}/hr
                                </span>
                              )}
                              {service.fixed_price && (
                                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-medium">
                                  ${Number(service.fixed_price).toLocaleString()}/fixed
                                </span>
                              )}
                              {service.per_hole_price && (
                                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-medium">
                                  ${Number(service.per_hole_price).toLocaleString()}/hole
                                </span>
                              )}
                              {service.night_price && (
                                <span className="text-xs bg-slate-700 text-white px-2 py-1 rounded-full font-medium">
                                  ${Number(service.night_price).toLocaleString()}/night
                                </span>
                              )}
                              {service.linear_footage_price && (
                                <span className="text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded-full font-medium">
                                  ${Number(service.linear_footage_price).toLocaleString()}/LF
                                </span>
                              )}
                            </div>
                          )}
                          <Badge variant={service.active ? "default" : "secondary"} className="mt-2">
                            {service.active ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button variant="ghost" size="sm" onClick={() => handleEditService(service)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedService(service); setShowServiceDeleteDialog(true); }}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Classifications Tab */}
      {activeTab === 'classifications' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button 
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
              onClick={handleAddClassification}
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Classification
            </Button>
          </div>

          {classifications.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="py-12 text-center">
                <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-medium text-slate-900 mb-1">No classifications</h3>
                <p className="text-slate-500">Add industry classifications for your clients</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {classifications.map(classification => (
                <Card key={classification.id} className="border-slate-200 hover:border-blue-300 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-semibold text-slate-900 truncate">{classification.name}</h3>
                          {classification.description && (
                            <p className="text-sm text-slate-500 line-clamp-2 mt-1">{classification.description}</p>
                          )}
                          <Badge variant={classification.active ? "default" : "secondary"} className="mt-2">
                            {classification.active ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button variant="ghost" size="sm" onClick={() => handleEditClassification(classification)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedClassification(classification); setShowClassificationDeleteDialog(true); }}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Proposal Types Tab */}
      {activeTab === 'proposalTypes' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button 
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
              onClick={handleAddProposalType}
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Proposal Type
            </Button>
          </div>

          {proposalTypes.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-medium text-slate-900 mb-1">No proposal types</h3>
                <p className="text-slate-500">Add proposal types to categorize your proposals</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {proposalTypes.map(proposalType => (
                <Card key={proposalType.id} className="border-slate-200 hover:border-purple-300 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <FileText className="w-5 h-5 text-purple-600" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-semibold text-slate-900 truncate">{proposalType.name}</h3>
                          {proposalType.description && (
                            <p className="text-sm text-slate-500 line-clamp-2 mt-1">{proposalType.description}</p>
                          )}
                          <Badge variant={proposalType.active ? "default" : "secondary"} className="mt-2">
                            {proposalType.active ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button variant="ghost" size="sm" onClick={() => handleEditProposalType(proposalType)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedProposalType(proposalType); setShowProposalTypeDeleteDialog(true); }}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Job Statuses Tab */}
      {activeTab === 'jobStatuses' && (
        <div className="space-y-4" data-testid="job-statuses-content">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Job Status Categories</h2>
              <p className="text-sm text-slate-500">Drag to reorder or use arrows to change order</p>
            </div>
            <Button 
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
              onClick={handleAddJobStatus}
              data-testid="add-job-status-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Job Status
            </Button>
          </div>

          {jobStatuses.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-medium text-slate-900 mb-1">No job statuses</h3>
                <p className="text-slate-500">Add job statuses to categorize your job workflow</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {jobStatuses.map((jobStatus, index) => (
                <Card 
                  key={jobStatus.id} 
                  className="border-slate-200 hover:border-blue-300 transition-colors"
                  data-testid={`job-status-${jobStatus.id}`}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        {/* Order Number */}
                        <div className="flex flex-col items-center gap-0.5">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 w-6 p-0"
                            onClick={() => moveJobStatus(index, 'up')}
                            disabled={index === 0}
                          >
                            <ChevronUp className="w-4 h-4" />
                          </Button>
                          <span className="text-xs font-bold text-slate-400 w-6 text-center">{index + 1}</span>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 w-6 p-0"
                            onClick={() => moveJobStatus(index, 'down')}
                            disabled={index === jobStatuses.length - 1}
                          >
                            <ChevronDown className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        {/* Status Info */}
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 border border-slate-200"
                          style={{ backgroundColor: jobStatus.color || '#DBEAFE' }}
                          title={jobStatus.color || 'No color set'}
                        >
                          <GripVertical className="w-5 h-5 text-slate-700 opacity-60" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-slate-900">{jobStatus.name}</h3>
                            {jobStatus.color && (
                              <span
                                className="inline-block px-2 py-0.5 rounded-full text-xs font-medium border"
                                style={{ backgroundColor: jobStatus.color, color: '#0f172a', borderColor: 'rgba(0,0,0,0.08)' }}
                              >
                                Preview
                              </span>
                            )}
                          </div>
                          {jobStatus.description && (
                            <p className="text-sm text-slate-500 line-clamp-1">{jobStatus.description}</p>
                          )}
                        </div>
                        <Badge variant={jobStatus.active ? "default" : "secondary"} className="flex-shrink-0">
                          {jobStatus.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button variant="ghost" size="sm" onClick={() => handleEditJobStatus(jobStatus)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedJobStatus(jobStatus); setShowJobStatusDeleteDialog(true); }}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Equipment Categories Tab */}
      {activeTab === 'equipment-categories' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Equipment Categories</h2>
              <p className="text-sm text-slate-500">Manage categories for equipment (used in Equipment dropdown)</p>
            </div>
            <Button 
              className="bg-amber-500 hover:bg-amber-400 text-slate-900"
              onClick={handleAddEquipmentCategory}
              data-testid="add-equipment-category-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </Button>
          </div>

          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="p-4 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h3 className="font-medium text-slate-900">Overdue Checkout Alerts</h3>
                <p className="text-sm text-slate-500">Notify managers automatically when equipment stays checked out this many days.</p>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  value={overdueThreshold}
                  onChange={(e) => setOverdueThreshold(e.target.value)}
                  className="w-20"
                  data-testid="overdue-threshold-input"
                />
                <span className="text-sm text-slate-600">days</span>
                <Button
                  onClick={handleSaveOverdueThreshold}
                  disabled={savingOverdueThreshold}
                  size="sm"
                  className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                  data-testid="save-overdue-threshold-btn"
                >
                  {savingOverdueThreshold ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {loadingEquipmentCategories ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
            </div>
          ) : equipmentCategories.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Truck className="w-12 h-12 text-slate-300 mb-4" />
                <p className="text-slate-500 mb-4">No equipment categories yet</p>
                <Button onClick={handleAddEquipmentCategory} className="bg-amber-500 hover:bg-amber-400 text-slate-900">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Category
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {equipmentCategories.map((category, index) => (
                <Card key={category.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        {/* Reorder Controls - same as Job Statuses */}
                        <div className="flex flex-col items-center gap-0">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 w-6 p-0"
                            onClick={() => moveEquipmentCategory(index, 'up')}
                            disabled={index === 0}
                          >
                            <ChevronUp className="w-4 h-4" />
                          </Button>
                          <span className="text-xs font-bold text-slate-400 w-6 text-center">{index + 1}</span>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 w-6 p-0"
                            onClick={() => moveEquipmentCategory(index, 'down')}
                            disabled={index === equipmentCategories.length - 1}
                          >
                            <ChevronDown className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        {/* Category Info - same layout as Job Statuses */}
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <GripVertical className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold text-slate-900">{category.name}</h3>
                          {category.description && (
                            <p className="text-sm text-slate-500 line-clamp-1">{category.description}</p>
                          )}
                          {Array.isArray(category.custom_fields) && category.custom_fields.length > 0 && (
                            <p className="text-xs text-amber-700 mt-1">
                              {category.custom_fields.length} custom field{category.custom_fields.length === 1 ? '' : 's'}
                            </p>
                          )}
                        </div>
                        <Badge variant={category.active ? "default" : "secondary"} className="flex-shrink-0">
                          {category.active ? 'Active' : 'Inactive'}
                        </Badge>
                        {(() => {
                          const subs = subcategoriesFor(category.id);
                          return subs.length > 0 ? (
                            <Badge variant="outline" className="flex-shrink-0" data-testid={`subcategory-count-${category.id}`}>
                              {subs.length} subcategor{subs.length === 1 ? 'y' : 'ies'}
                            </Badge>
                          ) : null;
                        })()}
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleCategoryExpansion(category.id)}
                          data-testid={`toggle-subcategories-${category.id}`}
                          title={expandedCategoryIds.has(category.id) ? 'Hide subcategories' : 'Show subcategories'}
                        >
                          {expandedCategoryIds.has(category.id)
                            ? <ChevronUp className="w-4 h-4" />
                            : <ChevronDown className="w-4 h-4" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleEditEquipmentCategory(category)} data-testid={`edit-equipment-category-${category.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedEquipmentCategory(category); setShowEquipmentCategoryDeleteDialog(true); }} data-testid={`delete-equipment-category-${category.id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>

                    {/* Subcategory panel — collapsed by default. Owns the
                        two checklist template linkages (Check-Out, which
                        also drives Pre-Trip for vehicles / Check-In).
                        Templates no longer live at the category level;
                        category-level template fields are kept in the DB
                        as an unused no-op.
                    */}
                    {expandedCategoryIds.has(category.id) && (
                      <div className="mt-3 ml-12 pl-4 border-l-2 border-blue-200 space-y-2">
                        {subcategoriesFor(category.id).length === 0 ? (
                          <div className="text-sm text-slate-500 italic py-2">
                            No subcategories yet. Add one to link a Check-Out / Check-In template to a specific piece of equipment.
                          </div>
                        ) : (
                          subcategoriesFor(category.id).map(sub => {
                            const linked = [
                              sub.checkout_template_id ? 'Check-Out (also drives Pre-Trip)' : null,
                              sub.checkin_template_id ? 'Check-In' : null,
                            ].filter(Boolean);
                            const coTpl = checklistTemplates.find(t => t.id === sub.checkout_template_id);
                            const ciTpl = checklistTemplates.find(t => t.id === sub.checkin_template_id);
                            const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
                            return (
                              <div
                                key={sub.id}
                                className="flex items-center justify-between gap-3 p-2 rounded bg-slate-50 border border-slate-200"
                                data-testid={`subcategory-row-${sub.id}`}
                              >
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className={`font-medium text-sm ${sub.active ? 'text-slate-900' : 'text-slate-400 line-through'}`}>
                                      {sub.name}
                                    </span>
                                    {!sub.active && (
                                      <Badge variant="secondary" className="text-xs">Inactive</Badge>
                                    )}
                                  </div>
                                  <div className="text-xs text-slate-500 mt-0.5">
                                    {linked.length > 0
                                      ? `Templates linked: ${linked.join(', ')}`
                                      : 'No templates linked'}
                                  </div>
                                  {(coTpl?.updated_at || ciTpl?.updated_at) && (
                                    <div className="text-[11px] text-slate-400 mt-0.5" data-testid={`subcategory-updated-${sub.id}`}>
                                      {coTpl?.updated_at && `Check-Out updated: ${fmtDate(coTpl.updated_at)}`}
                                      {coTpl?.updated_at && ciTpl?.updated_at && ' · '}
                                      {ciTpl?.updated_at && `Check-In updated: ${fmtDate(ciTpl.updated_at)}`}
                                    </div>
                                  )}
                                </div>
                                <div className="flex gap-1 flex-shrink-0">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setPreviewSubcategory(sub)}
                                    title="View live checklist"
                                    data-testid={`preview-subcategory-${sub.id}`}
                                  >
                                    <Eye className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleEditSubcategory(sub, category)}
                                    data-testid={`edit-subcategory-${sub.id}`}
                                  >
                                    <Edit className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => { setSelectedSubcategory(sub); setShowSubcategoryDeleteDialog(true); }}
                                    data-testid={`delete-subcategory-${sub.id}`}
                                  >
                                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                  </Button>
                                </div>
                              </div>
                            );
                          })
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleAddSubcategory(category)}
                          className="mt-2"
                          data-testid={`add-subcategory-${category.id}`}
                        >
                          <Plus className="w-3.5 h-3.5 mr-1" />
                          Add Subcategory
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Maintenance & Field Report Manager Settings Tab */}
      {activeTab === 'maintenance-settings' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Maintenance &amp; Field Report Manager Settings
            </h2>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-['Barlow_Condensed']">Assign Approvers &amp; Notification Recipients</CardTitle>
              <p className="text-sm text-slate-500">
                <strong>Maintenance / Field Report</strong> = who can approve those submissions.{' '}
                <strong>Maint Pings</strong> = field tech receives in-app + email when their own maintenance / equipment form is approved or rejected (default ON).{' '}
                <strong>FR Pings</strong> = receive "ready for packaging" notifications when any Field Report is approved (Paula / Becky / Lauren style).{' '}
                <strong>Weekly Archive</strong> = receive the Saturday-night Field Reports Archive email (PreCon/Soft Dig/Locate/Confined Space zip) — independent of approval rights above.{' '}
                <strong>CSE Submit / CSE Approve</strong> = who may create or approve a Confined Space Evaluation permit. Any number of people can be on either list; a person can be on both, but can never approve a specific report they personally submitted.
              </p>
            </CardHeader>
            <CardContent>
              {loadingMaintenanceSettings ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                </div>
              ) : (
                <div className="space-y-3">
                  {allUsers.length === 0 ? (
                    <p className="text-slate-500 text-center py-4">
                      No active users found. Add team members from the Team page to assign them as approvers here.
                    </p>
                  ) : (
                    allUsers.map(user => {
                      const isMaint = maintenanceSettings.manager_ids?.includes(user.id);
                      const isField = fieldReportSettings.manager_ids?.includes(user.id);
                      const isArchiveRecipient = archiveRecipientSettings.manager_ids?.includes(user.id);
                      const isCseSubmitter = cseRestrictions.submitter_ids?.includes(user.id);
                      const isCseApprover = cseRestrictions.approver_ids?.includes(user.id);
                      return (
                        <div
                          key={user.id}
                          className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                            (isMaint || isField) ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                          }`}
                          data-testid={`approver-row-${user.id}`}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-amber-700 font-medium">
                                {user.full_name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?'}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-slate-800 truncate">{user.full_name}</p>
                              <p className="text-sm text-slate-500 truncate">{user.email} • {user.role}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 sm:gap-6 flex-shrink-0">
                            <label className="flex flex-col items-center gap-0.5 sm:flex-row sm:gap-2 cursor-pointer" title="Approve maintenance check-in/out forms">
                              <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500 sm:hidden">MA</span>
                              <span className="text-xs text-slate-600 hidden sm:inline">Maintenance</span>
                              <Switch
                                checked={isMaint || false}
                                onCheckedChange={() => toggleMaintenanceManager(user.id)}
                                disabled={savingMaintenanceSettings}
                                data-testid={`maint-toggle-${user.id}`}
                              />
                            </label>
                            <label className="flex flex-col items-center gap-0.5 sm:flex-row sm:gap-2 cursor-pointer" title="Receive in-app + email pings on the maintenance / equipment forms THIS user submits (default ON)">
                              <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500 sm:hidden">RM</span>
                              <span className="text-xs text-slate-600 hidden sm:inline">Maint Pings</span>
                              <Switch
                                checked={(user.permissions?.can_receive_maintenance_notifications ?? true)}
                                onCheckedChange={() => toggleNotificationFlag(user.id, 'can_receive_maintenance_notifications')}
                                data-testid={`maint-pings-toggle-${user.id}`}
                              />
                            </label>
                            <label className="flex flex-col items-center gap-0.5 sm:flex-row sm:gap-2 cursor-pointer" title="Approve Field Reports">
                              <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500 sm:hidden">FR</span>
                              <span className="text-xs text-slate-600 hidden sm:inline">Field Report</span>
                              <Switch
                                checked={isField || false}
                                onCheckedChange={() => toggleFieldReportManager(user.id)}
                                disabled={savingFieldReportSettings}
                                data-testid={`field-report-toggle-${user.id}`}
                              />
                            </label>
                            <label className="flex flex-col items-center gap-0.5 sm:flex-row sm:gap-2 cursor-pointer" title="Receive 'ready for packaging' pings when ANYONE'S Field Report is approved (Paula / Becky / Lauren)">
                              <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500 sm:hidden">RF</span>
                              <span className="text-xs text-slate-600 hidden sm:inline">FR Pings</span>
                              <Switch
                                checked={Boolean(user.permissions?.can_receive_approval_notifications)}
                                onCheckedChange={() => toggleNotificationFlag(user.id, 'can_receive_approval_notifications')}
                                data-testid={`fr-pings-toggle-${user.id}`}
                              />
                            </label>
                            <label className="flex flex-col items-center gap-0.5 sm:flex-row sm:gap-2 cursor-pointer" title="Receive the Saturday-night Field Reports Archive email (zip of approved PreCon/Soft Dig/Locate/Confined Space PDFs) — separate from approval rights">
                              <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500 sm:hidden">WA</span>
                              <span className="text-xs text-slate-600 hidden sm:inline">Weekly Archive</span>
                              <Switch
                                checked={isArchiveRecipient || false}
                                onCheckedChange={() => toggleArchiveRecipient(user.id)}
                                disabled={savingArchiveRecipientSettings}
                                data-testid={`archive-recipient-toggle-${user.id}`}
                              />
                            </label>
                            <label className="flex flex-col items-center gap-0.5 sm:flex-row sm:gap-2 cursor-pointer" title="May create a new Confined Space Evaluation permit">
                              <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500 sm:hidden">CS</span>
                              <span className="text-xs text-slate-600 hidden sm:inline">CSE Submit</span>
                              <Switch
                                checked={isCseSubmitter || false}
                                onCheckedChange={() => toggleCseSubmitter(user.id)}
                                disabled={savingCseRestrictions}
                                data-testid={`cse-submit-toggle-${user.id}`}
                              />
                            </label>
                            <label className="flex flex-col items-center gap-0.5 sm:flex-row sm:gap-2 cursor-pointer" title="May approve/reject a Confined Space Evaluation permit (never their own submission)">
                              <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500 sm:hidden">CA</span>
                              <span className="text-xs text-slate-600 hidden sm:inline">CSE Approve</span>
                              <Switch
                                checked={isCseApprover || false}
                                onCheckedChange={() => toggleCseApprover(user.id)}
                                disabled={savingCseRestrictions}
                                data-testid={`cse-approve-toggle-${user.id}`}
                              />
                            </label>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {maintenanceSettings.managers?.length > 0 && (
              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-4">
                  <h3 className="font-medium text-green-800 mb-2">Maintenance Approvers</h3>
                  <div className="flex flex-wrap gap-2" data-testid="maintenance-approvers-chips">
                    {maintenanceSettings.managers.map(manager => (
                      <Badge key={manager.id} className="bg-green-100 text-green-800 border-green-300">
                        {manager.full_name}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            {fieldReportSettings.managers?.length > 0 && (
              <Card className="border-blue-200 bg-blue-50">
                <CardContent className="p-4">
                  <h3 className="font-medium text-blue-800 mb-2">Field Report Approvers</h3>
                  <div className="flex flex-wrap gap-2">
                    {fieldReportSettings.managers.map(manager => (
                      <Badge key={manager.id} className="bg-blue-100 text-blue-800 border-blue-300">
                        {manager.full_name}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            {cseRestrictions.submitters?.length > 0 && (
              <Card className="border-amber-200 bg-amber-50">
                <CardContent className="p-4">
                  <h3 className="font-medium text-amber-800 mb-2">Confined Space Submitters</h3>
                  <div className="flex flex-wrap gap-2" data-testid="cse-submitters-chips">
                    {cseRestrictions.submitters.map(u => (
                      <Badge key={u.id} className="bg-amber-100 text-amber-800 border-amber-300">
                        {u.full_name}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            {cseRestrictions.approvers?.length > 0 && (
              <Card className="border-orange-200 bg-orange-50">
                <CardContent className="p-4">
                  <h3 className="font-medium text-orange-800 mb-2">Confined Space Approvers</h3>
                  <div className="flex flex-wrap gap-2" data-testid="cse-approvers-chips">
                    {cseRestrictions.approvers.map(u => (
                      <Badge key={u.id} className="bg-orange-100 text-orange-800 border-orange-300">
                        {u.full_name}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Field Form Options Tab — manage option lists for Locate + SoftDig
          multi-select checkbox fields. Admins can add/rename/remove/reorder
          options; existing reports keep their saved values (legacy values
          surface as "removed" badges on the editing form). */}
      {activeTab === 'field-form-options' && (
        <div data-testid="field-form-options-tab-content">
          <FieldFormOptionsPanel />
        </div>
      )}

      {/* Job Edit Layout Tab */}
      {activeTab === 'job-edit-layout' && (
        <div className="space-y-6" data-testid="job-edit-layout-panel">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Eye className="w-5 h-5 text-amber-500" />
                Job Edit Layout
              </h2>
              <p className="text-sm text-slate-500 mt-1 max-w-2xl">
                Choose which modules appear on the Job Detail page. Toggle <strong>Global</strong> off to hide a module
                for everyone, or fine-tune visibility per role. Admins and Super Users always edit; toggles control display only.
              </p>
            </div>
            <Button
              onClick={handleSaveJobLayout}
              disabled={savingJobLayout || loadingJobLayout || !(isAdmin() || hasPermission('manage_services'))}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold"
              data-testid="save-job-layout-btn"
            >
              {savingJobLayout ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
              ) : (
                <><Save className="w-4 h-4 mr-2" /> Save Layout</>
              )}
            </Button>
          </div>

          <Card className="border-slate-200">
            <CardContent className="p-0">
              {loadingJobLayout ? (
                <div className="p-8 text-center text-slate-500">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                  Loading…
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left font-semibold text-slate-700 px-4 py-3">Module</th>
                        <th className="text-center font-semibold text-slate-700 px-4 py-3">Global</th>
                        <th className="text-center font-semibold text-slate-700 px-4 py-3">Admin</th>
                        <th className="text-center font-semibold text-slate-700 px-4 py-3">Manager</th>
                        <th className="text-center font-semibold text-slate-700 px-4 py-3">Technician</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(jobLayoutSettings.module_meta || []).map((meta, idx) => {
                        const m = jobLayoutSettings.modules?.[meta.key] || { global: true, admin: true, manager: true, technician: true };
                        const globalOff = m.global === false;
                        return (
                          <tr
                            key={meta.key}
                            className={`border-b border-slate-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}
                            data-testid={`job-layout-row-${meta.key}`}
                          >
                            <td className="px-4 py-3 font-medium text-slate-800">{meta.label}</td>
                            {['global', 'admin', 'manager', 'technician'].map(col => (
                              <td key={col} className="text-center px-4 py-3">
                                <Switch
                                  checked={!!m[col]}
                                  onCheckedChange={() => handleJobLayoutToggle(meta.key, col)}
                                  disabled={col !== 'global' && globalOff}
                                  data-testid={`job-layout-toggle-${meta.key}-${col}`}
                                />
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                      {(!jobLayoutSettings.module_meta || jobLayoutSettings.module_meta.length === 0) && (
                        <tr>
                          <td colSpan={5} className="px-4 py-8 text-center text-slate-400">No modules configured.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="text-xs text-slate-500 max-w-2xl">
            <p className="font-medium text-slate-600 mb-1">How it works</p>
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Global</strong> off → hides the module for everyone (overrides role toggles).</li>
              <li>Per-role toggles control whether users with that role see the module.</li>
              <li>Use the “Test as User” button on the Team page to preview changes as a Technician.</li>
            </ul>
          </div>
        </div>
      )}

      {/* Checklist Templates Tab (admin-only) */}
      {activeTab === 'checklist-templates' && (isAdmin() || isSuperUser?.()) && (
        <div className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />
              Form & Checklist Templates
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Edit the check-out, check-in, and pre-trip inspection templates that
              equipment subcategories link to. Historical submissions carry a frozen
              snapshot — changes only affect future submissions.
            </p>
          </div>
          <ChecklistTemplateEditor />
        </div>
      )}

      {/* Styleguide Tab */}
      {activeTab === 'styleguide' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Palette className="w-5 h-5" />
              UI Styleguide
            </h2>
            <Button 
              variant="outline"
              onClick={() => window.open('/styleguide', '_blank')}
            >
              Open Full Page
            </Button>
          </div>

          <Card>
            <CardContent className="p-6">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto bg-amber-100 rounded-full flex items-center justify-center">
                  <Palette className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900">UI Component Reference</h3>
                <p className="text-slate-600 max-w-md mx-auto">
                  View all available UI components, color palette, typography, and layout patterns used throughout the application.
                </p>
                <Button 
                  className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                  onClick={() => window.location.href = '/styleguide'}
                >
                  <Palette className="w-4 h-4 mr-2" />
                  Open Styleguide
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card className="hover:border-amber-300 transition-colors cursor-pointer" onClick={() => window.location.href = '/styleguide#typography'}>
              <CardContent className="p-4">
                <h4 className="font-medium text-slate-900">Typography</h4>
                <p className="text-sm text-slate-500">Headings, body text, labels</p>
              </CardContent>
            </Card>
            <Card className="hover:border-amber-300 transition-colors cursor-pointer" onClick={() => window.location.href = '/styleguide#colors'}>
              <CardContent className="p-4">
                <h4 className="font-medium text-slate-900">Colors</h4>
                <p className="text-sm text-slate-500">Brand palette, status colors</p>
              </CardContent>
            </Card>
            <Card className="hover:border-amber-300 transition-colors cursor-pointer" onClick={() => window.location.href = '/styleguide#buttons'}>
              <CardContent className="p-4">
                <h4 className="font-medium text-slate-900">Buttons</h4>
                <p className="text-sm text-slate-500">Primary, secondary, icon buttons</p>
              </CardContent>
            </Card>
            <Card className="hover:border-amber-300 transition-colors cursor-pointer" onClick={() => window.location.href = '/styleguide#forms'}>
              <CardContent className="p-4">
                <h4 className="font-medium text-slate-900">Form Fields</h4>
                <p className="text-sm text-slate-500">Inputs, selects, toggles</p>
              </CardContent>
            </Card>
            <Card className="hover:border-amber-300 transition-colors cursor-pointer" onClick={() => window.location.href = '/styleguide#tables'}>
              <CardContent className="p-4">
                <h4 className="font-medium text-slate-900">Tables</h4>
                <p className="text-sm text-slate-500">Data tables with actions</p>
              </CardContent>
            </Card>
            <Card className="hover:border-amber-300 transition-colors cursor-pointer" onClick={() => window.location.href = '/styleguide#layout'}>
              <CardContent className="p-4">
                <h4 className="font-medium text-slate-900">Page Layouts</h4>
                <p className="text-sm text-slate-500">PageLayout component usage</p>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Service Dialog */}
      <Dialog open={showServiceDialog} onOpenChange={setShowServiceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedService ? 'Edit Service' : 'Add Service'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Service Name *</Label>
              <Input
                value={serviceForm.name}
                onChange={(e) => setServiceForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Utility Locating"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={serviceForm.description}
                onChange={(e) => setServiceForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Service description..."
                rows={3}
              />
            </div>
            
            {/* Pricing Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Day Rate ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={serviceForm.day_price}
                  onChange={(e) => setServiceForm(prev => ({ ...prev, day_price: e.target.value }))}
                  placeholder="e.g., 1500.00"
                />
                <p className="text-xs text-slate-500">Price per day</p>
              </div>
              <div className="space-y-2">
                <Label>Hourly Rate ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={serviceForm.hourly_price}
                  onChange={(e) => setServiceForm(prev => ({ ...prev, hourly_price: e.target.value }))}
                  placeholder="e.g., 175.00"
                />
                <p className="text-xs text-slate-500">Price per hour</p>
              </div>
              <div className="space-y-2">
                <Label>Fixed Rate ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={serviceForm.fixed_price}
                  onChange={(e) => setServiceForm(prev => ({ ...prev, fixed_price: e.target.value }))}
                  placeholder="e.g., 500.00"
                />
                <p className="text-xs text-slate-500">Fixed price</p>
              </div>
              <div className="space-y-2">
                <Label>Per Hole Rate ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={serviceForm.per_hole_price}
                  onChange={(e) => setServiceForm(prev => ({ ...prev, per_hole_price: e.target.value }))}
                  placeholder="e.g., 150.00"
                />
                <p className="text-xs text-slate-500">Price per hole (Soft Dig)</p>
              </div>
              <div className="space-y-2">
                <Label>Night Rate ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={serviceForm.night_price}
                  onChange={(e) => setServiceForm(prev => ({ ...prev, night_price: e.target.value }))}
                  placeholder="e.g., 2000.00"
                  data-testid="service-night-price-input"
                />
                <p className="text-xs text-slate-500">Price per night</p>
              </div>
              <div className="space-y-2">
                <Label>Linear Footage Rate ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={serviceForm.linear_footage_price}
                  onChange={(e) => setServiceForm(prev => ({ ...prev, linear_footage_price: e.target.value }))}
                  placeholder="e.g., 12.50"
                  data-testid="service-linear-footage-price-input"
                />
                <p className="text-xs text-slate-500">Price per linear foot</p>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch
                checked={serviceForm.active}
                onCheckedChange={(checked) => setServiceForm(prev => ({ ...prev, active: checked }))}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowServiceDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveService} disabled={savingService} className="bg-amber-500 hover:bg-amber-400 text-slate-900">
                {savingService && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {selectedService ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Classification Dialog */}
      <Dialog open={showClassificationDialog} onOpenChange={setShowClassificationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedClassification ? 'Edit Classification' : 'Add Classification'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Classification Name *</Label>
              <Input
                value={classificationForm.name}
                onChange={(e) => setClassificationForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Construction"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={classificationForm.description}
                onChange={(e) => setClassificationForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Classification description..."
                rows={3}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch
                checked={classificationForm.active}
                onCheckedChange={(checked) => setClassificationForm(prev => ({ ...prev, active: checked }))}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowClassificationDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveClassification} disabled={savingClassification} className="bg-amber-500 hover:bg-amber-400 text-slate-900">
                {savingClassification && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {selectedClassification ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Service Delete Dialog */}
      <AlertDialog open={showServiceDeleteDialog} onOpenChange={setShowServiceDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Service</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{selectedService?.name}&rdquo;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteService} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Classification Delete Dialog */}
      <AlertDialog open={showClassificationDeleteDialog} onOpenChange={setShowClassificationDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Classification</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{selectedClassification?.name}&rdquo;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteClassification} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Proposal Type Dialog */}
      <Dialog open={showProposalTypeDialog} onOpenChange={setShowProposalTypeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedProposalType ? 'Edit Proposal Type' : 'Add Proposal Type'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Proposal Type Name *</Label>
              <Input
                value={proposalTypeForm.name}
                onChange={(e) => setProposalTypeForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Locate"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={proposalTypeForm.description}
                onChange={(e) => setProposalTypeForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Proposal type description..."
                rows={3}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch
                checked={proposalTypeForm.active}
                onCheckedChange={(checked) => setProposalTypeForm(prev => ({ ...prev, active: checked }))}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowProposalTypeDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveProposalType} disabled={savingProposalType} className="bg-amber-500 hover:bg-amber-400 text-slate-900">
                {savingProposalType && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {selectedProposalType ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Proposal Type Delete Dialog */}
      <AlertDialog open={showProposalTypeDeleteDialog} onOpenChange={setShowProposalTypeDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Proposal Type</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{selectedProposalType?.name}&rdquo;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteProposalType} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Job Status Dialog */}
      <Dialog open={showJobStatusDialog} onOpenChange={setShowJobStatusDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedJobStatus ? 'Edit Job Status' : 'Add Job Status'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Status Name *</Label>
              <Input
                value={jobStatusForm.name}
                onChange={(e) => setJobStatusForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Ready to schedule"
                data-testid="job-status-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={jobStatusForm.description}
                onChange={(e) => setJobStatusForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Job status description..."
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <p className="text-xs text-slate-500">Pick a pastel preset, or use the color picker for a custom shade.</p>
              <div className="flex flex-wrap gap-2 pt-1">
                {[
                  '#FECACA', // pastel red
                  '#FED7AA', // pastel orange
                  '#FDE68A', // pastel yellow
                  '#D9F99D', // pastel lime
                  '#BBF7D0', // pastel green
                  '#A7F3D0', // pastel teal
                  '#BAE6FD', // pastel sky
                  '#BFDBFE', // pastel blue
                  '#C7D2FE', // pastel indigo
                  '#DDD6FE', // pastel violet
                  '#F5D0FE', // pastel fuchsia
                  '#FBCFE8', // pastel pink
                  '#E5E7EB', // pastel slate
                ].map(c => (
                  <button
                    key={c}
                    type="button"
                    aria-label={`Use color ${c}`}
                    onClick={() => setJobStatusForm(prev => ({ ...prev, color: c }))}
                    className={`w-8 h-8 rounded-full border-2 transition ${jobStatusForm.color === c ? 'border-slate-900 ring-2 ring-slate-300' : 'border-white shadow'}`}
                    style={{ backgroundColor: c }}
                    data-testid={`color-swatch-${c.slice(1)}`}
                  />
                ))}
              </div>
              <div className="flex items-center gap-3 pt-1">
                <input
                  type="color"
                  value={jobStatusForm.color || '#FDE68A'}
                  onChange={(e) => setJobStatusForm(prev => ({ ...prev, color: e.target.value }))}
                  className="h-9 w-14 rounded border border-slate-200 cursor-pointer"
                  data-testid="color-picker"
                />
                <Input
                  value={jobStatusForm.color || ''}
                  onChange={(e) => setJobStatusForm(prev => ({ ...prev, color: e.target.value }))}
                  placeholder="#FDE68A"
                  className="font-mono w-32"
                />
                <span
                  className="ml-auto inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border"
                  style={{ backgroundColor: jobStatusForm.color || '#FDE68A', color: '#0f172a', borderColor: 'rgba(0,0,0,0.08)' }}
                >
                  {jobStatusForm.name || 'Preview'}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch
                checked={jobStatusForm.active}
                onCheckedChange={(checked) => setJobStatusForm(prev => ({ ...prev, active: checked }))}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowJobStatusDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveJobStatus} disabled={savingJobStatus} className="bg-amber-500 hover:bg-amber-400 text-slate-900">
                {savingJobStatus && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {selectedJobStatus ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Job Status Delete Dialog */}
      <AlertDialog open={showJobStatusDeleteDialog} onOpenChange={setShowJobStatusDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Job Status</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{selectedJobStatus?.name}&rdquo;? This action cannot be undone.
              Jobs using this status will keep their current status value.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteJobStatus} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Equipment Category Dialog */}
      <Dialog open={showEquipmentCategoryDialog} onOpenChange={setShowEquipmentCategoryDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedEquipmentCategory ? 'Edit Equipment Category' : 'Add Equipment Category'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Category Name *</Label>
              <Input
                value={equipmentCategoryForm.name}
                onChange={(e) => setEquipmentCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Trucks, Trailers, Drones"
                data-testid="equipment-category-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Input
                value={equipmentCategoryForm.description}
                onChange={(e) => setEquipmentCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of this category"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={equipmentCategoryForm.active}
                onCheckedChange={(checked) => setEquipmentCategoryForm(prev => ({ ...prev, active: checked }))}
              />
              <Label>Active (visible in dropdowns)</Label>
            </div>

            {/* Feb 2026 — Field-use toggle. Categories with this OFF
                (Shop Tools and Equipment, Forklift, Skid Steer by
                default) never appear in the Check-Out / Check-In
                equipment picker but stay listed everywhere else
                (equipment database, Grease / Maintenance Request
                forms). */}
            <div className="flex items-center gap-2">
              <Switch
                checked={equipmentCategoryForm.field_use !== false}
                onCheckedChange={(checked) => setEquipmentCategoryForm(prev => ({ ...prev, field_use: checked }))}
                data-testid="equipment-category-field-use-switch"
              />
              <Label>Available for Check-Out / Check-In (turn off for shop-only equipment)</Label>
            </div>

            {/* NOTE: Checklist template linkage used to live here at the
                category level. As of Feb 2026 it moved to the
                subcategory level (see the expandable subcategory panel
                on each category row). Category-level template fields
                remain in the DB as a no-op — they're not read anywhere
                and this dialog no longer sets them. */}

            {/* Custom Fields editor */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <Label className="text-base">Custom Fields</Label>
                  <p className="text-xs text-slate-500 mt-1">
                    Extra fields shown when adding equipment of this category. Useful for things like Calibration Date, Hose Length, Last Inspection Date.
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addCustomField}
                  data-testid="custom-field-add-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Field
                </Button>
              </div>

              {(equipmentCategoryForm.custom_fields || []).length === 0 && (
                <p className="text-sm text-slate-400 italic py-3 text-center bg-slate-50 rounded">
                  No custom fields. Click "Add Field" to define one.
                </p>
              )}

              <div className="space-y-3">
                {(equipmentCategoryForm.custom_fields || []).map((field, idx) => (
                  <div
                    key={field.id || idx}
                    className="border border-slate-200 rounded-lg p-3 bg-slate-50/60"
                    data-testid={`custom-field-row-${idx}`}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                      <div className="md:col-span-4">
                        <Label className="text-xs">Label</Label>
                        <Input
                          value={field.label || ''}
                          onChange={(e) => updateCustomField(idx, { label: e.target.value })}
                          placeholder="e.g., Calibration Date"
                          className="h-9"
                          data-testid={`custom-field-label-${idx}`}
                        />
                      </div>
                      <div className="md:col-span-3">
                        <Label className="text-xs">Type</Label>
                        <Select
                          value={field.field_type || 'text'}
                          onValueChange={(v) => updateCustomField(idx, { field_type: v })}
                        >
                          <SelectTrigger className="h-9" data-testid={`custom-field-type-${idx}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="text">Text</SelectItem>
                            <SelectItem value="number">Number</SelectItem>
                            <SelectItem value="date">Date</SelectItem>
                            <SelectItem value="dropdown">Dropdown</SelectItem>
                            <SelectItem value="checkbox">Checkbox</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="md:col-span-4">
                        {field.field_type === 'dropdown' && (
                          <>
                            <Label className="text-xs">Options (comma-separated)</Label>
                            <Input
                              value={Array.isArray(field.options) ? field.options.join(', ') : (field.options || '')}
                              onChange={(e) => updateCustomField(idx, { options: e.target.value.split(',').map(s => s.trim()) })}
                              placeholder="Pass, Fail, N/A"
                              className="h-9"
                            />
                          </>
                        )}
                        {field.field_type === 'date' && (
                          <>
                            <Label className="text-xs">Reminder days before (optional)</Label>
                            <Input
                              type="number"
                              min="0"
                              value={field.reminder_days_before ?? ''}
                              onChange={(e) => updateCustomField(idx, { reminder_days_before: e.target.value === '' ? null : parseInt(e.target.value) })}
                              placeholder="e.g. 14"
                              className="h-9"
                            />
                            <div className="flex items-center gap-2 mt-2">
                              <Switch
                                checked={!!field.requires_assignee}
                                onCheckedChange={(c) => updateCustomField(idx, { requires_assignee: c })}
                                data-testid={`custom-field-assignee-${idx}`}
                              />
                              <Label className="text-xs">Require assignee (team member responsible)</Label>
                            </div>
                          </>
                        )}
                      </div>
                      <div className="md:col-span-1 flex items-end">
                        <div className="flex flex-col items-center w-full">
                          <Label className="text-xs">Req.</Label>
                          <Switch
                            checked={!!field.required}
                            onCheckedChange={(c) => updateCustomField(idx, { required: c })}
                            className="mt-1"
                            data-testid={`custom-field-required-${idx}`}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end mt-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => removeCustomField(idx)}
                        className="h-8 text-red-600 hover:bg-red-50"
                        data-testid={`custom-field-remove-${idx}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1" />
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEquipmentCategoryDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveEquipmentCategory} disabled={savingEquipmentCategory} className="bg-amber-500 hover:bg-amber-400 text-slate-900" data-testid="equipment-category-save-btn">
              {savingEquipmentCategory ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {selectedEquipmentCategory ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Equipment Category Delete Dialog */}
      <AlertDialog open={showEquipmentCategoryDeleteDialog} onOpenChange={setShowEquipmentCategoryDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Equipment Category</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedEquipmentCategory?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteEquipmentCategory} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Subcategory Dialog — carries the three checklist template
          dropdowns that used to live on the Category dialog. */}
      <Dialog open={showSubcategoryDialog} onOpenChange={setShowSubcategoryDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedSubcategory ? 'Edit Subcategory' : 'Add Subcategory'}
              {selectedSubcategoryParent && (
                <span className="text-sm text-slate-500 font-normal ml-2">
                  {' under '}{selectedSubcategoryParent.name}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Subcategory Name *</Label>
              <Input
                value={subcategoryForm.name}
                onChange={(e) => setSubcategoryForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Dually, Rover Camera, Ditch Witch"
                data-testid="subcategory-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Input
                value={subcategoryForm.description}
                onChange={(e) => setSubcategoryForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of this subcategory"
                data-testid="subcategory-description-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={subcategoryForm.active}
                onCheckedChange={(checked) => setSubcategoryForm(prev => ({ ...prev, active: checked }))}
                data-testid="subcategory-active-switch"
              />
              <Label>Active (visible in the Equipment form's Subcategory picker)</Label>
            </div>

            {/* Two template dropdowns — the whole point of the
                subcategory layer. Blank = fall back to placeholder.
                2026-08-28 — owner correction: Pre-Trip and Check-Out
                are the same event (Pre-Trip is just the vehicle-flavored
                name for it), so there's ONE "Check Out Template" here,
                not three separate pickers implying they're independent. */}
            <div className="border-t pt-4">
              <div className="mb-2">
                <Label className="text-base">Checklist Templates</Label>
                <p className="text-xs text-slate-500 mt-1">
                  Pick which form each action uses for equipment tagged with this subcategory. "Check Out Template" also drives Pre-Trip Inspection — they're the same checklist for this equipment type. Leave any blank if the current placeholder should still apply.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { key: 'checkout_template_id', label: 'Check Out Template' },
                  { key: 'checkin_template_id', label: 'Check In Template' },
                ].map(({ key, label }) => (
                  <div key={key}>
                    <Label className="text-sm">{label}</Label>
                    <Select
                      value={subcategoryForm[key] ?? '__unassigned__'}
                      onValueChange={(v) => setSubcategoryForm(prev => ({
                        ...prev,
                        [key]: v === '__unassigned__' ? null : v,
                      }))}
                    >
                      <SelectTrigger data-testid={`subcategory-${key.replace(/_/g, '-')}-select`}>
                        <SelectValue placeholder="Unassigned" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__unassigned__" data-testid={`sub-${key}-option-unassigned`}>
                          Unassigned (use current placeholder)
                        </SelectItem>
                        {checklistTemplates.map(t => (
                          <SelectItem
                            key={t.id}
                            value={t.id}
                            data-testid={`sub-${key}-option-${t.id}`}
                          >
                            {t.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubcategoryDialog(false)}>Cancel</Button>
            <Button
              onClick={handleSaveSubcategory}
              disabled={savingSubcategory}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900"
              data-testid="subcategory-save-btn"
            >
              {savingSubcategory ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {selectedSubcategory ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Round 16 (2026-09-04) — read-only live checklist preview.
          Shows exactly what a tech sees at Check-Out/Check-In/Pre-Trip
          for this subcategory, including the "Updated" date marker,
          without needing to touch a real asset. */}
      <Dialog open={!!previewSubcategory} onOpenChange={(open) => !open && setPreviewSubcategory(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="subcategory-preview-dialog">
          <DialogHeader>
            <DialogTitle>Live Checklist Preview — {previewSubcategory?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pt-2">
            {['checkout', 'checkin'].map((slot) => {
              const tpl = checklistTemplates.find(t => t.id === previewSubcategory?.[`${slot}_template_id`]);
              const label = slot === 'checkout' ? 'Check-Out (also drives Pre-Trip)' : 'Check-In';
              return (
                <div key={slot} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-sm text-slate-700">{label}</h4>
                    {tpl?.updated_at && (
                      <span className="text-xs text-slate-400">
                        Updated: {new Date(tpl.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    )}
                  </div>
                  {!tpl ? (
                    <p className="text-sm text-slate-500 italic">
                      No template linked — a tech sees the generic "Standard {slot === 'checkout' ? 'Check-Out' : 'Check-In'} Checklist" placeholder.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm font-medium text-slate-800">{tpl.title}</p>
                      {(tpl.sections || []).map((section, sIdx) => (
                        <div key={sIdx}>
                          {section.name && <p className="text-xs font-semibold text-slate-500 uppercase mb-1">{section.name}</p>}
                          <ul className="space-y-1" data-testid={`preview-${slot}-items-${sIdx}`}>
                            {(section.items || []).map((item, iIdx) => (
                              <li key={iIdx} className="text-sm text-slate-700 flex items-start gap-1.5">
                                <span className="text-slate-400 mt-0.5">•</span>
                                <span>
                                  {item.label}
                                  {item.required && <span className="text-red-500"> *</span>}
                                  {item.photo && <span className="text-xs text-slate-400"> (photo)</span>}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewSubcategory(null)} data-testid="subcategory-preview-close-btn">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subcategory Delete Dialog — the backend will reject the
          delete if assets are attached; we surface the "deactivate
          instead" hint via the error toast. */}
      <AlertDialog open={showSubcategoryDeleteDialog} onOpenChange={setShowSubcategoryDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Subcategory</AlertDialogTitle>
            <AlertDialogDescription>
              Delete "{selectedSubcategory?.name}"? If any assets are tagged with this subcategory, the deletion will be blocked — deactivate it instead so the checklist linkage stops applying to new check-outs while keeping historical asset records intact.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDeleteSubcategory}
              className="bg-red-600 hover:bg-red-700"
              data-testid="subcategory-confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Equipment Dialog */}


      {/* Equipment Delete Dialog */}


      {/* Restore Confirmation Dialog */}
      <AlertDialog open={showRestoreConfirm} onOpenChange={setShowRestoreConfirm}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-amber-500" />
              Restore from Backup
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>You are about to restore data from:</p>
                <div className="p-3 bg-slate-100 rounded-lg text-slate-900">
                  <p className="font-medium">{selectedBackupForRestore?.filename}</p>
                  <p className="text-sm text-slate-600">
                    {selectedBackupForRestore?.total_records} records • {selectedBackupForRestore?.created_at ? new Date(selectedBackupForRestore.created_at).toLocaleString() : ''}
                  </p>
                </div>

                {/* Collection picker */}
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-800" data-testid="restore-picker-header">
                      Select collections to restore
                      {backupPreview?.collections && (
                        <span className="ml-2 text-xs text-slate-500">
                          ({Object.values(backupPreview.collections).filter(c => c.restorable).length} restorable)
                        </span>
                      )}
                    </span>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          if (!backupPreview?.collections) return;
                          const all = Object.entries(backupPreview.collections)
                            .filter(([, meta]) => meta.restorable).map(([n]) => n);
                          setSelectedCollections(new Set(all));
                        }}
                        className="text-xs text-indigo-700 hover:text-indigo-900 font-medium"
                        data-testid="restore-picker-select-all"
                      >Select all</button>
                      <button
                        type="button"
                        onClick={() => setSelectedCollections(new Set())}
                        className="text-xs text-slate-600 hover:text-slate-900 font-medium"
                        data-testid="restore-picker-clear"
                      >Clear</button>
                    </div>
                  </div>
                  <div className="max-h-64 overflow-y-auto p-2 space-y-1" data-testid="restore-picker-list">
                    {previewLoading && <p className="text-sm text-slate-500 py-4 text-center">Loading backup manifest...</p>}
                    {backupPreview?.error && <p className="text-sm text-rose-700 py-4 text-center">Failed to load manifest — check backend logs.</p>}
                    {backupPreview?.collections && Object.entries(backupPreview.collections)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([name, meta]) => (
                        <label
                          key={name}
                          className={`flex items-center gap-2 px-2 py-1 rounded text-sm ${meta.restorable ? 'hover:bg-slate-100 cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
                          data-testid={`restore-picker-row-${name}`}
                        >
                          <input
                            type="checkbox"
                            disabled={!meta.restorable}
                            checked={selectedCollections.has(name)}
                            onChange={(e) => {
                              const next = new Set(selectedCollections);
                              if (e.target.checked) next.add(name); else next.delete(name);
                              setSelectedCollections(next);
                            }}
                            data-testid={`restore-picker-checkbox-${name}`}
                          />
                          <span className="flex-1 font-mono text-xs">{name}</span>
                          <span className="text-xs text-slate-500">{meta.count} docs</span>
                          {!meta.restorable && (
                            <span className="text-xs px-1.5 py-0.5 bg-rose-100 text-rose-700 rounded">excluded</span>
                          )}
                        </label>
                      ))}
                  </div>
                </div>

                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                  <p className="font-medium flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" /> How this works:
                  </p>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li>Only the collections you tick above will be restored</li>
                    <li>Existing records with the same ID are skipped (no overwrites)</li>
                    <li>New records missing from the live DB get inserted</li>
                    <li>Collections marked <span className="font-mono">excluded</span> can never be restored (live credentials, transient state, etc.)</li>
                  </ul>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="restore-picker-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRestoreConfirm}
              disabled={selectedCollections.size === 0 || previewLoading}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 disabled:opacity-50"
              data-testid="restore-picker-confirm"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Restore {selectedCollections.size} collection{selectedCollections.size === 1 ? '' : 's'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <div className="mt-8 pt-4 border-t border-slate-100 text-center">
        <Link to="/privacy-policy" className="text-xs text-slate-400 hover:text-slate-600" data-testid="privacy-policy-link">
          Privacy Policy
        </Link>
      </div>
    </div>
  );
}
